
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Sparkles, Wand2, Eye, EyeOff, Plus, Trash2, Copy, ArrowLeft, CheckCircle2, ArrowUp, ArrowDown, RotateCcw, RotateCw, ListTree } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';
import { app } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { formService } from '../../services/formService';
import { storage } from '../../config/firebase';
import SmartSensorField from './SmartSensorField';

const DEFAULT_DOC_CONFIG = {
  headerText: 'Smart Processed Document',
  footerText: 'ParrotDocs Smart Processing',
  showHeader: true,
  showFooter: true,
  showNavigationMenu: false,
  navigationMenuTitle: 'Document Navigation'
};
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const HEADER_SPACE_PX = 56;
const FOOTER_SPACE_PX = 56;
const PAGE_CONTENT_PADDING_TOP_PX = 24;
const PAGE_CONTENT_PADDING_BOTTOM_PX = 24;
const TEXT_LAYOUT_SAFETY_PX = 28;
const DEFAULT_SMART_CHIP_CONFIG = {
  enabled: false,
  enableText: true,
  enableRating: false,
  enableProgress: false,
  enableStatus: false,
  chipPosition: 'bottom-right',
  chipStyle: 'default'
};

const COMPONENT_TYPES = [
  { type: 'heading', label: 'Heading' },
  { type: 'textBlock', label: 'Text Block' },
  { type: 'richContent', label: 'Rich Content' },
  { type: 'sectionDivider', label: 'Section Divider' },
  { type: 'pageBreak', label: 'Page Break' },
  { type: 'image', label: 'Image Upload' },
  { type: 'textInput', label: 'Text Input' },
  { type: 'textareaInput', label: 'Textarea' },
  { type: 'dropdown', label: 'Dropdown' },
  { type: 'radio', label: 'Radio Group' },
  { type: 'checklist', label: 'Check List' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'datePicker', label: 'Date Picker' },
  { type: 'barcodeInput', label: 'Barcode Scanner' },
  { type: 'qrCodeInput', label: 'QR Code Scanner' },
  { type: 'nfcInput', label: 'NFC Reader' },
  { type: 'progress', label: 'Progress' },
  { type: 'rating', label: 'Rating' },
  { type: 'trafficLightStatus', label: 'Traffic Light Status' },
  { type: 'submitButton', label: 'Submit Button' }
];

const TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank Smart Doc',
    description: 'Start from scratch and build your own structure.',
    components: []
  },
  {
    id: 'employee-onboarding',
    name: 'Employee Onboarding',
    description: 'HR-focused onboarding with guided fields.',
    components: [
      { type: 'heading', props: { content: 'Employee Onboarding Form', level: 1, alignment: 'center' } },
      { type: 'textBlock', props: { content: 'Please complete the details below to finalize onboarding.' } },
      { type: 'textInput', props: { label: 'Full Name', placeholder: 'Jane Doe', required: true, variableName: 'full_name' } },
      { type: 'textInput', props: { label: 'Email Address', placeholder: 'jane@company.com', required: true, variableName: 'email' } },
      { type: 'datePicker', props: { label: 'Start Date', variableName: 'start_date' } },
      { type: 'dropdown', props: { label: 'Department', options: ['Engineering', 'Sales', 'Operations'], variableName: 'department' } },
      { type: 'checkbox', props: { label: 'I agree to company policies', required: true, variableName: 'policy_accept' } },
      { type: 'submitButton', props: { buttonText: 'Submit Onboarding' } }
    ]
  },
  {
    id: 'patient-intake',
    name: 'Patient Intake',
    description: 'Healthcare intake with conditional visibility support.',
    components: [
      { type: 'heading', props: { content: 'Patient Intake Form', level: 1, alignment: 'center' } },
      { type: 'textInput', props: { label: 'Full Name', required: true, variableName: 'patient_name' } },
      { type: 'datePicker', props: { label: 'Date of Birth', variableName: 'dob' } },
      { type: 'dropdown', props: { label: 'Visit Type', options: ['Routine', 'Emergency', 'Follow-up'], variableName: 'visit_type' } },
      {
        type: 'textareaInput',
        props: {
          label: 'Emergency Details',
          variableName: 'emergency_details',
          visibilityLogic: {
            action: 'show',
            condition: 'all',
            rules: [{ fieldId: 'visit_type', operator: 'equals', value: 'Emergency' }]
          }
        }
      },
      { type: 'submitButton', props: { buttonText: 'Submit Intake' } }
    ]
  }
];

const DEFAULT_PROPS = {
  heading: { content: 'New Heading', level: 2, alignment: 'left', textSize: '2xl', width: 'full' },
  textBlock: { content: 'Add paragraph content here...', alignment: 'left', textSize: 'base', width: 'full' },
  richContent: {
    content: 'Paste formatted notes, links, or YouTube URLs here...',
    contentHtml: '',
    alignment: 'left',
    textSize: 'base',
    width: 'full',
    media: []
  },
  sectionDivider: {
    content: 'Section',
    alignment: 'left',
    textSize: 'sm',
    width: 'full',
    lineStyle: 'solid',
    lineColor: '#cbd5e1',
    lineThickness: 1,
    spacingTop: 0,
    spacingBottom: 0,
    showLabel: true
  },
  pageBreak: { width: 'full' },
  image: {
    src: 'https://picsum.photos/seed/parrotdocs/960/480',
    alt: 'Uploaded image',
    alignment: 'left',
    width: 'full',
    cropScale: 1,
    cropX: 50,
    cropY: 50
  },
  textInput: { label: 'Text Input', placeholder: 'Enter text...', required: false, value: '', variableName: 'text_input', alignment: 'left', textSize: 'sm', width: 'full' },
  textareaInput: { label: 'Textarea Input', placeholder: 'Enter details...', required: false, value: '', rows: 3, variableName: 'textarea_input', alignment: 'left', textSize: 'sm', width: 'full' },
  dropdown: { label: 'Dropdown', options: ['Option 1', 'Option 2'], required: false, value: '', variableName: 'dropdown', alignment: 'left', textSize: 'sm', width: 'full' },
  radio: { label: 'Radio Group', options: ['Option 1', 'Option 2'], required: false, value: '', variableName: 'radio_group', alignment: 'left', textSize: 'sm', width: 'full' },
  checklist: { label: 'Check List', options: ['Item 1', 'Item 2'], required: false, value: [], variableName: 'check_list', alignment: 'left', textSize: 'sm', width: 'full' },
  checkbox: { label: 'Checkbox', required: false, value: 'false', variableName: 'checkbox', alignment: 'left', textSize: 'sm', width: 'full' },
  datePicker: { label: 'Pick Date', value: '', variableName: 'date', alignment: 'left', textSize: 'sm', width: 'full' },
  barcodeInput: { label: 'Barcode', placeholder: 'Scan barcode or type value...', required: false, value: '', variableName: 'barcode_value', alignment: 'left', textSize: 'sm', width: 'full' },
  qrCodeInput: { label: 'QR Code', placeholder: 'Scan QR code or type value...', required: false, value: '', variableName: 'qr_code_value', alignment: 'left', textSize: 'sm', width: 'full' },
  nfcInput: { label: 'NFC Tag', placeholder: 'Tap NFC tag or type value...', required: false, value: '', variableName: 'nfc_value', alignment: 'left', textSize: 'sm', width: 'full' },
  progress: { label: 'Progress', progressValue: 55, progressColor: '#4f46e5', alignment: 'left', textSize: 'sm', width: 'full' },
  rating: { label: 'Rating', value: '0', maxRating: 5, variableName: 'rating', alignment: 'left', textSize: 'sm', width: 'full' },
  trafficLightStatus: {
    label: 'Status',
    value: 'orange',
    variableName: 'status',
    alignment: 'left',
    textSize: 'sm',
    width: 'full'
  },
  submitButton: { buttonText: 'Submit', alignment: 'left', textSize: 'sm', width: 'full' }
};

const GROUP_COMPONENT_TYPE = 'componentGroup';

const id = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const toBindingKey = (value) => {
  const normalized = `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized;
};

const isBindableComponent = (type) => !['submitButton', 'sectionDivider', 'pageBreak'].includes(type);
const isGroupComponent = (component) => component?.type === GROUP_COMPONENT_TYPE;

const flattenComponents = (items = []) =>
  items.flatMap((component) => (isGroupComponent(component) ? flattenComponents(component.children || []) : [component]));

const buildNavigationItems = (items = []) =>
  flattenComponents(items)
    .filter((component) => (
      ['heading', 'sectionDivider'].includes(component?.type) &&
      `${component?.props?.content || ''}`.trim()
    ))
    .map((component, index) => ({
      id: component.id,
      label: `${component.props.content}`.trim(),
      type: component.type,
      level: component.props.level || (component.type === 'heading' ? 2 : 3),
      order: index + 1
    }));

const SmartNavigationPanel = ({ items = [], title = 'Document Navigation', disabled = false, fixedClass = 'lg:left-6 lg:top-24 lg:bottom-6' }) => (
  <aside className={`w-full shrink-0 lg:fixed lg:z-30 lg:w-72 ${fixedClass}`}>
    <nav className="max-h-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#232753]">
        <ListTree className="h-4 w-4 text-[#4f46e5]" />
        <span>{title || 'Document Navigation'}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">Add headings or section dividers to populate the navigation panel.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <a
              key={`studio-smart-nav-panel-${item.id}`}
              href={`#smart-section-${item.id}`}
              onClick={disabled ? (e) => e.preventDefault() : undefined}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-[#232753] hover:border-[#4f46e5] hover:bg-[#eef2ff]"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eef2ff] text-xs text-[#4f46e5]">
                {item.order}
              </span>
              <span className="min-w-0 truncate">{item.label}</span>
            </a>
          ))}
        </div>
      )}
    </nav>
  </aside>
);

const buildBindingLabel = (component, index) => {
  if (component?.props?.label) return component.props.label;
  if (component?.props?.content && component.type === 'heading') return component.props.content;
  if (component?.props?.content && component.type === 'textBlock') return component.props.content.slice(0, 42);
  return `${component.type || 'component'}_${index + 1}`;
};

const buildDataBindings = (allComponents) =>
  flattenComponents(allComponents)
    .map((component, index) => {
      if (!isBindableComponent(component.type)) return null;
      const binding = toBindingKey(component?.props?.variableName || '');
      if (!binding) return null;

      return {
        componentId: component.id,
        componentType: component.type,
        variableName: binding,
        label: buildBindingLabel(component, index),
        required: Boolean(component?.props?.required),
        hasVisibilityLogic: Boolean(component?.props?.visibilityLogic?.rules?.length)
      };
    })
    .filter(Boolean);

const modeFromOcrMode = (extractMode, currentLayoutMode) => {
  if (extractMode === 'text') return 'reflow';
  return currentLayoutMode === 'reflow' ? 'reflow' : 'original';
};

const withSmartChipDefaults = (component) => ({
  ...component,
  props: {
    ...(component?.props || {}),
    smartChipConfig: {
      ...DEFAULT_SMART_CHIP_CONFIG,
      ...(component?.props?.smartChipConfig || {})
    },
    smartChipData: {
      ...(component?.props?.smartChipData || {})
    }
  }
});

const normalizeToField = (component, index) => {
  const fallbackId = `field-${index + 1}`;
  const kind = component.type;
  const props = component.props || {};

  if (!['textInput', 'textareaInput', 'dropdown', 'radio', 'checklist', 'checkbox', 'datePicker', 'barcodeInput', 'qrCodeInput', 'nfcInput', 'trafficLightStatus'].includes(kind)) return null;

  const typeMap = {
    textInput: 'text',
    textareaInput: 'textarea',
    dropdown: 'select',
    radio: 'radio',
    checklist: 'checklist',
    checkbox: 'checkbox',
    datePicker: 'date',
    barcodeInput: 'text',
    qrCodeInput: 'text',
    nfcInput: 'text',
    trafficLightStatus: 'select'
  };

  return {
    id: props.variableName || fallbackId,
    label: props.label || component.type,
    type: typeMap[kind] || 'text',
    required: Boolean(props.required),
    placeholder: props.placeholder || ''
  };
};

const evalRule = (rule, value) => {
  const source = `${value ?? ''}`;
  const target = `${rule.value ?? ''}`;

  switch (rule.operator) {
    case 'equals':
      return source === target;
    case 'notEquals':
      return source !== target;
    case 'contains':
      return source.toLowerCase().includes(target.toLowerCase());
    case 'notContains':
      return !source.toLowerCase().includes(target.toLowerCase());
    case 'greaterThan':
      return Number(source) > Number(target);
    case 'lessThan':
      return Number(source) < Number(target);
    case 'isEmpty':
      return source.trim() === '';
    case 'isNotEmpty':
      return source.trim() !== '';
    default:
      return false;
  }
};

const componentVisible = (component, allComponents) => {
  if (isGroupComponent(component)) return true;
  const logic = component.props?.visibilityLogic;
  if (!logic?.rules?.length) return true;

  const checks = logic.rules.map((rule) => {
    const targetComp = allComponents.find((c) => c.props?.variableName === rule.fieldId);
    const value = targetComp?.props?.value;
    return evalRule(rule, value);
  });

  const pass = logic.condition === 'any' ? checks.some(Boolean) : checks.every(Boolean);
  return logic.action === 'hide' ? !pass : pass;
};

const textSizeClassMap = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl'
};

const widthClassMap = {
  full: 'col-span-12',
  'two-thirds': 'col-span-12 md:col-span-8',
  half: 'col-span-12 md:col-span-6',
  third: 'col-span-12 md:col-span-4'
};
const widthSpanMap = {
  full: 12,
  'two-thirds': 8,
  half: 6,
  third: 4
};
const PAGE_INNER_WIDTH_PX = A4_WIDTH_PX - 96;
const GRID_GAP_PX = 8;

const getTextSizeClass = (size) => textSizeClassMap[size] || 'text-sm';
const getWidthClass = (width) => widthClassMap[width] || widthClassMap.full;
const getWidthSpan = (width) => widthSpanMap[width] || widthSpanMap.full;
const getPageBodyHeight = (showHeader = true, showFooter = true) =>
  A4_HEIGHT_PX - (showHeader ? HEADER_SPACE_PX : 0) - (showFooter ? FOOTER_SPACE_PX : 0);
const getPageContentHeight = (showHeader = true, showFooter = true) =>
  getPageBodyHeight(showHeader, showFooter) -
  PAGE_CONTENT_PADDING_TOP_PX -
  PAGE_CONTENT_PADDING_BOTTOM_PX -
  TEXT_LAYOUT_SAFETY_PX;
const textSizePxMap = {
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30
};

const stripMarkdown = (value = '') =>
  `${value}`
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/[*_~>#-]/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim();

const charsPerLineForWidth = (width = 'full', fontPx = 14) => {
  const baseMap = {
    full: 78,
    'two-thirds': 52,
    half: 38,
    third: 24
  };
  const base = baseMap[width] || baseMap.full;
  const adjustment = Math.round((16 - fontPx) * 1.3);
  return Math.max(12, base + adjustment);
};

const estimateRenderedLineCount = (text, width = 'full', fontPx = 14) => {
  const charsPerLine = charsPerLineForWidth(width, fontPx);
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n');
  const segments = normalized.split('\n');

  return segments.reduce((total, segment) => {
    const content = stripMarkdown(segment);
    return total + Math.max(1, Math.ceil((content.length || 0) / charsPerLine));
  }, 0);
};

const estimateTextHeight = (text, width = 'full', textSize = 'base', extra = 0) => {
  const fontPx = textSizePxMap[textSize] || 16;
  const lines = Math.max(1, estimateRenderedLineCount(text || ' ', width, fontPx));
  return Math.ceil(lines * (fontPx * 1.6) + extra);
};
const getTextBlockFontPx = (props = {}) => textSizePxMap[props.textSize] || 16;
const getGridColumnWidthPx = () => (PAGE_INNER_WIDTH_PX - (GRID_GAP_PX * 11)) / 12;
const getComponentRenderWidthPx = (width = 'full') => {
  const span = getWidthSpan(width);
  const columnWidth = getGridColumnWidthPx();
  return Math.round((columnWidth * span) + (GRID_GAP_PX * Math.max(0, span - 1)));
};

const measureTextBlockHeight = (text, props = {}) => {
  if (typeof document === 'undefined') {
    return estimateTextHeight(text, props.width || 'full', props.textSize || 'base', estimateTextBlockExtraHeight(text));
  }

  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    'visibility:hidden',
    `width:${getComponentRenderWidthPx(props.width || 'full')}px`,
    'box-sizing:border-box',
    `font-size:${getTextBlockFontPx(props)}px`,
    'line-height:1.5',
    'color:#334155',
    'overflow-wrap:anywhere',
    'word-break:break-word',
    'padding-top:1px'
  ].join(';');
  probe.innerHTML = getTextBlockHtml({ ...props, content: text, contentHtml: markdownToHtml(text) }) || '<p>&nbsp;</p>';
  document.body.appendChild(probe);

  try {
    return Math.ceil(probe.getBoundingClientRect().height);
  } finally {
    document.body.removeChild(probe);
  }
};

const orderedListPattern = /^(\d+)\.\s+(.+)$/;
const unorderedListPattern = /^[-*]\s+(.+)$/;
const thematicBreakPattern = /^(-{3,}|\*{3,}|_{3,})$/;

const estimateTextBlockExtraHeight = (text = '') => {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n').trim();
  if (!normalized) return 24;

  const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean);
  const headingCount = lines.filter((line) => /^#{1,6}\s+/.test(line)).length;
  const orderedCount = lines.filter((line) => orderedListPattern.test(line)).length;
  const unorderedCount = lines.filter((line) => unorderedListPattern.test(line)).length;
  const blockCount = normalized.split(/\n\s*\n/).map((chunk) => chunk.trim()).filter(Boolean).length;

  return 24 + (headingCount * 10) + ((orderedCount + unorderedCount) * 4) + (Math.max(0, blockCount - 1) * 10);
};

const parseTextFlowBlocks = (text) => {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n');
  if (!normalized.trim()) return [];

  return normalized
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
      if (lines.length && lines.every((line) => orderedListPattern.test(line))) {
        const firstMatch = lines[0].match(orderedListPattern);
        const startNumber = firstMatch ? Number(firstMatch[1]) : 1;
        return {
          type: 'orderedList',
          items: lines.map((line, itemIndex) => {
            const match = line.match(orderedListPattern);
            return { number: startNumber + itemIndex, text: match[2] };
          })
        };
      }
      if (lines.length && lines.every((line) => unorderedListPattern.test(line))) {
        return {
          type: 'unorderedList',
          items: lines.map((line) => {
            const match = line.match(unorderedListPattern);
            return { text: match[1] };
          })
        };
      }
      if (lines.length === 1 && thematicBreakPattern.test(lines[0])) {
        return { type: 'divider' };
      }
      return { type: 'text', text: chunk };
    });
};

const textFlowBlockToText = (block) => {
  if (!block) return '';
  if (block.type === 'orderedList') {
    return block.items.map((item) => `${item.number}. ${item.text}`).join('\n');
  }
  if (block.type === 'unorderedList') {
    return block.items.map((item) => `- ${item.text}`).join('\n');
  }
  if (block.type === 'divider') {
    return '---';
  }
  return block.text || '';
};

const joinTextFlowBlocks = (blocks) =>
  blocks
    .map((block) => textFlowBlockToText(block))
    .filter(Boolean)
    .join('\n\n')
    .trim();

const splitInlineTextByRenderedLines = (text, maxLines, width = 'full', fontPx = 14) => {
  const normalized = `${text || ''}`.replace(/\r\n/g, '\n');
  if (!normalized) return { firstText: '', restText: '' };

  const charsPerLine = charsPerLineForWidth(width, fontPx);
  let remainingLines = Math.max(1, maxLines);
  let firstText = '';
  let cursor = 0;

  while (cursor < normalized.length && remainingLines > 0) {
    const newlineIndex = normalized.indexOf('\n', cursor);
    const segmentEnd = newlineIndex === -1 ? normalized.length : newlineIndex;
    const rawSegment = normalized.slice(cursor, segmentEnd);
    const renderedSegment = stripMarkdown(rawSegment);
    const segmentLines = Math.max(1, Math.ceil((renderedSegment.length || 0) / charsPerLine));

    if (segmentLines <= remainingLines) {
      firstText += rawSegment;
      remainingLines -= segmentLines;

      if (newlineIndex !== -1) {
        firstText += '\n';
        cursor = newlineIndex + 1;
      } else {
        cursor = normalized.length;
      }
      continue;
    }

    const maxChars = Math.max(charsPerLine, remainingLines * charsPerLine);
    let splitAt = rawSegment.lastIndexOf(' ', maxChars);
    if (splitAt < maxChars * 0.45) splitAt = maxChars;
    firstText += rawSegment.slice(0, splitAt);
    cursor += splitAt;
    break;
  }

  return {
    firstText: firstText.trimEnd(),
    restText: normalized.slice(cursor).trimStart()
  };
};

const splitTextByRenderedLines = (text, maxLines, width = 'full', fontPx = 14) => {
  const blocks = parseTextFlowBlocks(text);
  if (!blocks.length) return { firstText: '', restText: '' };

  const accepted = [];
  let usedLines = 0;

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    const blockText = textFlowBlockToText(block);
    const blockLines = estimateRenderedLineCount(blockText, width, fontPx);
    const nextBlock = blocks[index + 1] || null;
    const nextBlockLines = nextBlock
      ? estimateRenderedLineCount(textFlowBlockToText(nextBlock), width, fontPx)
      : 0;

    if (block.type === 'divider' && nextBlock && accepted.length > 0) {
      if (usedLines + blockLines + nextBlockLines > maxLines) {
        return {
          firstText: joinTextFlowBlocks(accepted),
          restText: joinTextFlowBlocks(blocks.slice(index))
        };
      }
    }

    if (usedLines + blockLines <= maxLines) {
      accepted.push(block);
      usedLines += blockLines;
      continue;
    }

    if (!accepted.length) {
      if (block.type === 'orderedList' || block.type === 'unorderedList') {
        const firstItems = [];
        let itemLines = 0;
        for (let itemIndex = 0; itemIndex < block.items.length; itemIndex += 1) {
          const item = block.items[itemIndex];
          const itemText = block.type === 'orderedList' ? `${item.number}. ${item.text}` : `- ${item.text}`;
          const currentLines = estimateRenderedLineCount(itemText, width, fontPx);
          if (itemLines + currentLines <= maxLines) {
            firstItems.push(item);
            itemLines += currentLines;
            continue;
          }

          if (!firstItems.length) {
            const prefix = block.type === 'orderedList' ? `${item.number}. ` : '- ';
            const split = splitInlineTextByRenderedLines(`${prefix}${item.text}`, maxLines, width, fontPx);
            const stripPrefix = (value) => (
              value.startsWith(prefix)
                ? value.slice(prefix.length).trimStart()
                : value.trimStart()
            );
            const firstPartText = stripPrefix(split.firstText);
            const restPartText = stripPrefix(split.restText);

            if (firstPartText) {
              firstItems.push(
                block.type === 'orderedList'
                  ? { number: item.number, text: firstPartText }
                  : { text: firstPartText }
              );
            }

            const restItems = [];
            if (restPartText) {
              restItems.push(
                block.type === 'orderedList'
                  ? { number: item.number, text: restPartText }
                  : { text: restPartText }
              );
            }
            restItems.push(...block.items.slice(itemIndex + 1));
            return {
              firstText: joinTextFlowBlocks([{ ...block, items: firstItems }]),
              restText: joinTextFlowBlocks([{ ...block, items: restItems }, ...blocks.slice(index + 1)])
            };
          }
          return {
            firstText: joinTextFlowBlocks([{ ...block, items: firstItems }]),
            restText: joinTextFlowBlocks([{ ...block, items: block.items.slice(itemIndex) }, ...blocks.slice(index + 1)])
          };
        }
      } else {
        const split = splitInlineTextByRenderedLines(blockText, maxLines, width, fontPx);
        return {
          firstText: split.firstText,
          restText: joinTextFlowBlocks([{ type: 'text', text: split.restText }, ...blocks.slice(index + 1)])
        };
      }
    }
    return {
      firstText: joinTextFlowBlocks(accepted),
      restText: joinTextFlowBlocks(blocks.slice(index))
    };
  }

  return {
    firstText: joinTextFlowBlocks(accepted),
    restText: ''
  };
};

const splitTextToFitHeight = (text, availableHeight, props = {}) => {
  const source = `${text || ''}`;
  if (!source.trim()) return { firstText: source, restText: '' };

  const fullHeight = measureTextBlockHeight(source, props);
  if (fullHeight <= availableHeight) {
    return { firstText: source, restText: '', firstHeight: fullHeight };
  }

  const fontPx = getTextBlockFontPx(props);
  const width = props.width || 'full';
  const totalLines = Math.max(1, estimateRenderedLineCount(source, width, fontPx));
  let low = 1;
  let high = totalLines;
  let best = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = splitTextByRenderedLines(source, mid, width, fontPx);
    const firstText = `${candidate.firstText || ''}`;
    const restText = `${candidate.restText || ''}`;

    if (!firstText || firstText === source || !restText) {
      high = mid - 1;
      continue;
    }

    const measuredHeight = measureTextBlockHeight(firstText, props);
    if (measuredHeight <= availableHeight) {
      best = { firstText, restText, firstHeight: measuredHeight };
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (best) return best;

  const fallback = splitTextByRenderedLines(source, 1, width, fontPx);
  const fallbackFirst = `${fallback.firstText || ''}`;
  const fallbackRest = `${fallback.restText || ''}`;
  if (fallbackFirst && fallbackRest) {
    return {
      firstText: fallbackFirst,
      restText: fallbackRest,
      firstHeight: measureTextBlockHeight(fallbackFirst, props)
    };
  }

  return { firstText: source, restText: '', firstHeight: fullHeight };
};

const estimateComponentHeight = (component) => {
  const props = component?.props || {};
  const width = props.width || 'full';

  switch (component.type) {
    case 'heading': {
      const size = props.textSize || (props.level === 1 ? '3xl' : props.level === 2 ? '2xl' : 'xl');
      return estimateTextHeight(props.content || 'Heading', width, size, 24);
    }
    case 'textBlock': {
      const text = getTextBlockPlainText(props) || 'Text';
      return estimateTextHeight(text, width, props.textSize || 'base', estimateTextBlockExtraHeight(text));
    }
    case 'richContent': {
      const textHeight = estimateTextHeight(props.content || 'Rich content', width, props.textSize || 'base', 24);
      const mediaHeight = getRichMediaItems(props).reduce((total, item) => total + getRichMediaHeight(item) + 16, 0);
      return textHeight + mediaHeight + 12;
    }
    case 'sectionDivider':
      return Math.max(32, Number(props.spacingTop ?? 24) + Number(props.spacingBottom ?? 24) + (props.showLabel !== false && props.content ? 36 : 12));
    case 'navigationMenu':
      return Math.max(96, 68 + ((Array.isArray(props.items) ? props.items.length : 0) * 28));
    case 'image':
      return Math.max(120, Number(props.imgHeight) || 220) + 12;
    case 'textInput':
    case 'datePicker':
    case 'dropdown':
    case 'barcodeInput':
    case 'qrCodeInput':
    case 'nfcInput':
      return 84;
    case 'textareaInput':
      return 96 + (Math.max(0, Number(props.rows || 3) - 3) * 18);
    case 'checkbox':
      return 48;
    case 'radio':
    case 'checklist':
      return 56 + ((Array.isArray(props.options) ? props.options.length : 0) * 28);
    case 'progress':
      return 54;
    case 'rating':
      return 60;
    case 'trafficLightStatus':
      return 78;
    case GROUP_COMPONENT_TYPE:
      return 64 + estimateGroupedChildrenHeight(component.children || []);
    case 'submitButton':
      return 60;
    case 'pageBreak':
      return 32;
    default:
      return 72;
  }
};

const getRenderedComponentHeight = (component) => {
  if (component?.type === 'textBlock') {
    return measureTextBlockHeight(getTextBlockPlainText(component.props), component.props || {});
  }
  if (component?.type === 'richContent') {
    return measureRichContentHeight(component.props || {});
  }
  return estimateComponentHeight(component);
};

const estimateGroupedChildrenHeight = (children = []) => {
  let currentRowSpan = 0;
  let currentRowHeight = 0;
  let totalHeight = 0;

  children.forEach((child) => {
    const span = getWidthSpan(child?.props?.width || 'full');
    const height = getRenderedComponentHeight(child);
    if (currentRowSpan > 0 && currentRowSpan + span > 12) {
      totalHeight += currentRowHeight + GRID_GAP_PX;
      currentRowSpan = 0;
      currentRowHeight = 0;
    }

    currentRowSpan += span;
    currentRowHeight = Math.max(currentRowHeight, height);

    if (currentRowSpan >= 12) {
      totalHeight += currentRowHeight + GRID_GAP_PX;
      currentRowSpan = 0;
      currentRowHeight = 0;
    }
  });

  if (currentRowSpan > 0) {
    totalHeight += currentRowHeight;
  } else if (totalHeight > 0) {
    totalHeight = Math.max(0, totalHeight - GRID_GAP_PX);
  }

  return totalHeight;
};

const filterVisibleComponentTree = (items = [], allComponents = []) =>
  items
    .map((component) => {
      if (!isGroupComponent(component)) {
        return componentVisible(component, allComponents) ? component : null;
      }

      const visibleChildren = filterVisibleComponentTree(component.children || [], allComponents);
      if (visibleChildren.length === 0) return null;
      return {
        ...component,
        children: visibleChildren
      };
    })
    .filter(Boolean);

const createTextBlockFragment = (component, sourceText, fragmentStart, fragmentText) => {
  const safeText = `${fragmentText || ''}`;
  return {
    ...component,
    props: {
      ...(component.props || {}),
      content: safeText,
      contentHtml: markdownToHtml(safeText),
      fullContent: sourceText,
      isSplitFragment: true,
      fragmentStart,
      fragmentEnd: fragmentStart + safeText.length
    }
  };
};

const createRichContentFragment = (component, contentHtml, media, fragmentStart = 0) => {
  const safeHtml = `${contentHtml || ''}`;
  const fragmentText = htmlToPlainText(safeHtml);
  return {
    ...component,
    props: {
      ...(component.props || {}),
      contentHtml: safeHtml,
      content: fragmentText,
      media: Array.isArray(media) ? media : [],
      suppressEmptyContent: !safeHtml.trim(),
      isSplitFragment: true,
      fragmentStart,
      fragmentEnd: fragmentStart + getRichHtmlTextLength(safeHtml)
    }
  };
};

const paginateComponents = (
  components,
  { includePageBreakMarker = false, pageContentHeight = getPageContentHeight(), editingRichContentId = null } = {}
) => {
  const pages = [];
  let currentItems = [];
  let remainingHeight = pageContentHeight;
  let fragmentCursor = 0;
  let currentRowSpan = 0;
  let currentRowHeight = 0;

  const pushPage = () => {
    pages.push(currentItems);
    currentItems = [];
    remainingHeight = pageContentHeight;
    currentRowSpan = 0;
    currentRowHeight = 0;
  };

  const pushEntry = (entry, height) => {
    const span = getWidthSpan(entry?.renderComponent?.props?.width || entry?.component?.props?.width || 'full');
    const heightDelta = currentRowSpan === 0 ? height : Math.max(0, height - currentRowHeight);
    currentItems.push(entry);
    remainingHeight -= heightDelta;
    currentRowSpan += span;
    currentRowHeight = Math.max(currentRowHeight, height);

    if (currentRowSpan >= 12) {
      currentRowSpan = 0;
      currentRowHeight = 0;
    }
  };

  components.forEach((component) => {
    if (component.type === 'pageBreak') {
      if (includePageBreakMarker) {
        pushEntry(
          {
            key: `${component.id}-page-break`,
            component,
            renderComponent: component,
            isFragment: false
          },
          estimateComponentHeight(component)
        );
      }
      pushPage();
      return;
    }

    let pending = {
      component,
      renderComponent: component,
      isFragment: false
    };

    while (pending) {
      const widthSpan = getWidthSpan(pending.renderComponent?.props?.width || component?.props?.width || 'full');
      if (currentRowSpan > 0 && currentRowSpan + widthSpan > 12) {
        currentRowSpan = 0;
        currentRowHeight = 0;
      }

      const estimatedHeight = getRenderedComponentHeight(pending.renderComponent);
      const heightDelta = currentRowSpan === 0 ? estimatedHeight : Math.max(0, estimatedHeight - currentRowHeight);
      if (component.type === 'richContent' && component.id === editingRichContentId) {
        if (heightDelta > remainingHeight && currentItems.length > 0) {
          pushPage();
          continue;
        }
        pushEntry(
          {
            component,
            renderComponent: component,
            isFragment: false,
            key: `${component.id}-${fragmentCursor}`,
            allowInternalOverflow: estimatedHeight > remainingHeight
          },
          Math.min(estimatedHeight, remainingHeight)
        );
        fragmentCursor += 1;
        pending = null;
        continue;
      }
      if (heightDelta <= remainingHeight) {
        pushEntry(
          {
            ...pending,
            key: `${component.id}-${fragmentCursor}`,
            allowInternalOverflow: false
          },
          estimatedHeight
        );
        fragmentCursor += 1;
        pending = null;
        continue;
      }

      if (currentRowSpan > 0) {
        currentRowSpan = 0;
        currentRowHeight = 0;
        continue;
      }

      if (component.type === 'textBlock') {
        const pendingText = getTextBlockPlainText(pending.renderComponent?.props || component.props || {});
        const fullText = getTextBlockSource(pending.renderComponent?.props || component.props || {});
        const fragmentStart = Number(pending.renderComponent?.props?.fragmentStart) || 0;
        const split = splitTextToFitHeight(pendingText, remainingHeight, pending.renderComponent?.props || component.props || {});

        if (split.firstText && split.restText && split.firstText !== pendingText) {
          const firstFragment = createTextBlockFragment(component, fullText, fragmentStart, split.firstText);
          pushEntry(
            {
              component,
              renderComponent: firstFragment,
              isFragment: true,
              allowInternalOverflow: false,
              key: `${component.id}-${fragmentCursor}`
            },
            split.firstHeight || getRenderedComponentHeight(firstFragment)
          );
          fragmentCursor += 1;
          pushPage();
          pending = {
            component,
            renderComponent: createTextBlockFragment(component, fullText, fragmentStart + split.firstText.length, split.restText),
            isFragment: true
          };
          continue;
        }
      }

      if (component.type === 'richContent') {
        const pendingProps = pending.renderComponent?.props || component.props || {};
        const pendingHtml = getRichContentHtml(pendingProps);
        const pendingMedia = getRichMediaItems(pendingProps);
        const fragmentStart = Number(pendingProps.fragmentStart) || 0;
        const htmlHeight = pendingHtml.trim() ? Math.max(72, measureRichContentHtmlHeight(pendingHtml, pendingProps)) : 0;
        const split = pendingHtml.trim()
          ? splitRichHtmlToFitHeight(pendingHtml, remainingHeight, pendingProps)
          : { firstHtml: '', restHtml: '' };

        if (split.firstHtml && split.restHtml) {
          const firstFragment = createRichContentFragment(component, split.firstHtml, [], fragmentStart);
          pushEntry(
            {
              component,
              renderComponent: firstFragment,
              isFragment: true,
              allowInternalOverflow: false,
              key: `${component.id}-${fragmentCursor}`
            },
            getRenderedComponentHeight(firstFragment)
          );
          fragmentCursor += 1;
          pushPage();
          pending = {
            component,
            renderComponent: createRichContentFragment(
              component,
              split.restHtml,
              pendingMedia,
              fragmentStart + getRichHtmlTextLength(split.firstHtml)
            ),
            isFragment: true
          };
          continue;
        }

        if (htmlHeight <= remainingHeight && pendingMedia.length > 0) {
          let usedHeight = htmlHeight;
          let mediaCount = 0;
          while (mediaCount < pendingMedia.length) {
            const mediaHeight = getRichMediaHeight(pendingMedia[mediaCount]) + 24;
            if (usedHeight + mediaHeight > remainingHeight) break;
            usedHeight += mediaHeight;
            mediaCount += 1;
          }

          if (mediaCount > 0 || pendingHtml.trim()) {
            const firstFragment = createRichContentFragment(
              component,
              pendingHtml,
              pendingMedia.slice(0, mediaCount),
              fragmentStart
            );
            pushEntry(
              {
                component,
                renderComponent: firstFragment,
                isFragment: true,
                allowInternalOverflow: false,
                key: `${component.id}-${fragmentCursor}`
              },
              getRenderedComponentHeight(firstFragment)
            );
            fragmentCursor += 1;

            if (mediaCount < pendingMedia.length) {
              pushPage();
              pending = {
                component,
                renderComponent: createRichContentFragment(
                  component,
                  '',
                  pendingMedia.slice(mediaCount),
                  fragmentStart + getRichHtmlTextLength(pendingHtml)
                ),
                isFragment: true
              };
            } else {
              pending = null;
            }
            continue;
          }
        }
      }

      if (currentItems.length === 0) {
        pushEntry(
          {
            ...pending,
            key: `${component.id}-${fragmentCursor}`,
            allowInternalOverflow: ['textBlock', 'richContent'].includes(component.type)
          },
          estimatedHeight
        );
        fragmentCursor += 1;
        pending = null;
        continue;
      }

      pushPage();
    }
  });

  if (currentItems.length > 0 || pages.length === 0) {
    pages.push(currentItems);
  }

  return pages;
};

const escapeHtml = (value) =>
  `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderInlineMarkdown = (text) => {
  let html = escapeHtml(text);
  html = html.replace(/`([^`]+)`/g, '<code class="rounded bg-slate-100 px-1 py-0.5 text-[0.92em]">$1</code>');
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-[#4f46e5] underline">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  return html;
};

const splitMarkdownTableRow = (line = '') => {
  let source = `${line}`.trim();
  if (source.startsWith('|')) source = source.slice(1);
  if (source.endsWith('|') && !source.endsWith('\\|')) source = source.slice(0, -1);

  const cells = [];
  let cell = '';
  let inCode = false;

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (char === '\\' && source[index + 1] === '|') {
      cell += '|';
      index += 1;
      continue;
    }
    if (char === '`') inCode = !inCode;
    if (char === '|' && !inCode) {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    cell += char;
  }
  cells.push(cell.trim());
  return cells;
};

const getMarkdownTableAlignments = (line = '') => {
  if (!`${line}`.includes('|')) return null;
  const cells = splitMarkdownTableRow(line);
  if (!cells.length || cells.some((cell) => !/^:?-{3,}:?$/.test(cell.replace(/\s+/g, '')))) return null;

  return cells.map((cell) => {
    const marker = cell.replace(/\s+/g, '');
    if (marker.startsWith(':') && marker.endsWith(':')) return 'center';
    if (marker.endsWith(':')) return 'right';
    return 'left';
  });
};

const renderMarkdownTable = (headerCells, alignments, bodyRows) => {
  const columnCount = headerCells.length;
  const renderCell = (cell, index, tag) => {
    const alignment = alignments[index] || 'left';
    return `<${tag} style="text-align:${alignment};">${renderInlineMarkdown(cell || '')}</${tag}>`;
  };
  const normalizedRows = bodyRows.map((row) => Array.from({ length: columnCount }, (_, index) => row[index] || ''));

  return (
    '<div class="markdown-table-wrap">' +
      '<table>' +
        `<thead><tr>${headerCells.map((cell, index) => renderCell(cell, index, 'th')).join('')}</tr></thead>` +
        (normalizedRows.length
          ? `<tbody>${normalizedRows.map((row) => `<tr>${row.map((cell, index) => renderCell(cell, index, 'td')).join('')}</tr>`).join('')}</tbody>`
          : '') +
      '</table>' +
    '</div>'
  );
};

const markdownToHtml = (source) => {
  const lines = `${source ?? ''}`.replace(/\r\n?/g, '\n').split('\n');
  const html = [];
  let inUl = false;
  let inOl = false;
  let inCode = false;

  const nextContentLineIndex = (startIndex) => {
    let nextIndex = startIndex;
    while (nextIndex < lines.length && !lines[nextIndex].trim()) nextIndex += 1;
    return nextIndex;
  };

  const closeLists = () => {
    if (inUl) {
      html.push('</div>');
      inUl = false;
    }
    if (inOl) {
      html.push('</div>');
      inOl = false;
    }
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const line = rawLine.trimEnd();

    if (line.startsWith('```')) {
      closeLists();
      if (!inCode) {
        inCode = true;
        html.push('<pre class="mb-3 overflow-x-auto rounded-lg bg-slate-900 p-3 text-slate-100"><code>');
      } else {
        inCode = false;
        html.push('</code></pre>');
      }
      continue;
    }

    if (inCode) {
      html.push(`${escapeHtml(rawLine)}\n`);
      continue;
    }

    if (!line) {
      closeLists();
      continue;
    }

    const separatorIndex = nextContentLineIndex(lineIndex + 1);
    const tableAlignments = separatorIndex < lines.length
      ? getMarkdownTableAlignments(lines[separatorIndex].trim())
      : null;
    const tableHeader = tableAlignments ? splitMarkdownTableRow(line) : null;
    if (tableHeader && tableHeader.length === tableAlignments.length) {
      closeLists();
      const bodyRows = [];
      lineIndex = separatorIndex + 1;
      while (lineIndex < lines.length) {
        const bodyIndex = nextContentLineIndex(lineIndex);
        if (bodyIndex >= lines.length) {
          lineIndex = lines.length;
          break;
        }

        const bodyLine = lines[bodyIndex].trim();
        if (!bodyLine.includes('|')) {
          lineIndex = bodyIndex;
          break;
        }

        const possibleNextSeparatorIndex = nextContentLineIndex(bodyIndex + 1);
        if (
          possibleNextSeparatorIndex < lines.length &&
          getMarkdownTableAlignments(lines[possibleNextSeparatorIndex].trim())
        ) {
          lineIndex = bodyIndex;
          break;
        }

        bodyRows.push(splitMarkdownTableRow(bodyLine));
        lineIndex = bodyIndex + 1;
      }
      lineIndex -= 1;
      html.push(renderMarkdownTable(tableHeader, tableAlignments, bodyRows));
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      closeLists();
      const level = heading[1].length;
      html.push(`<h${level} class="mb-2 font-bold text-[#232753]">${renderInlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }

    const quote = line.match(/^>\s+(.+)$/);
    if (quote) {
      closeLists();
      html.push(`<blockquote class="mb-3 border-l-4 border-slate-300 pl-3 italic text-slate-600">${renderInlineMarkdown(quote[1])}</blockquote>`);
      continue;
    }

    if (thematicBreakPattern.test(line.trim())) {
      closeLists();
      html.push('<hr class="my-4 border-0 border-t border-slate-300" />');
      continue;
    }

    const ordered = line.match(/^(\d+)\.\s+(.+)$/);
    if (ordered) {
      if (!inOl) {
        if (inUl) {
          html.push('</div>');
          inUl = false;
        }
        html.push('<div class="mb-3" style="margin:0 0 0.75rem 0;">');
        inOl = true;
      }
      html.push(
        `<div class="mb-1" style="display:flex;align-items:flex-start;gap:0.625rem;margin:0 0 0.25rem 0;">` +
          `<span style="flex:0 0 1.6rem;min-width:1.6rem;text-align:right;">${ordered[1]}.</span>` +
          `<span style="flex:1;min-width:0;">${renderInlineMarkdown(ordered[2])}</span>` +
        `</div>`
      );
      continue;
    }

    const unordered = line.match(/^[-*]\s+(.+)$/);
    if (unordered) {
      if (!inUl) {
        if (inOl) {
          html.push('</div>');
          inOl = false;
        }
        html.push('<div class="mb-3" style="margin:0 0 0.75rem 0;">');
        inUl = true;
      }
      html.push(
        `<div class="mb-1" style="display:flex;align-items:flex-start;gap:0.625rem;margin:0 0 0.25rem 0;">` +
          `<span style="flex:0 0 1.6rem;min-width:1.6rem;text-align:center;">&bull;</span>` +
          `<span style="flex:1;min-width:0;">${renderInlineMarkdown(unordered[1])}</span>` +
        `</div>`
      );
      continue;
    }

    closeLists();
    html.push(`<p class="mb-3">${renderInlineMarkdown(line)}</p>`);
  }

  closeLists();
  if (inCode) html.push('</code></pre>');

  return html.join('');
};

const htmlToPlainText = (html) => {
  const source = `${html ?? ''}`.trim();
  if (!source) return '';

  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;

    const serializeNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.replace(/\s+/g, ' ') || '';
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return '';

      const tag = node.tagName.toLowerCase();
      const childText = Array.from(node.childNodes).map(serializeNode).join('');

      if (tag === 'table') {
        const rows = Array.from(node.querySelectorAll('tr'))
          .map((row) => Array.from(row.children)
            .filter((cell) => ['th', 'td'].includes(cell.tagName?.toLowerCase()))
            .map((cell) => `${cell.textContent || ''}`.trim().replace(/\|/g, '\\|')))
          .filter((row) => row.length);
        if (!rows.length) return '';

        const columnCount = Math.max(...rows.map((row) => row.length));
        const normalizeRow = (row) => Array.from({ length: columnCount }, (_, index) => row[index] || '');
        const separator = Array.from({ length: columnCount }, () => '---');
        return `${[normalizeRow(rows[0]), separator, ...rows.slice(1).map(normalizeRow)]
          .map((row) => `| ${row.join(' | ')} |`)
          .join('\n')}\n\n`;
      }

      if (/^h[1-6]$/.test(tag)) {
        const level = Number(tag.slice(1));
        return `${'#'.repeat(level)} ${childText.trim()}\n\n`;
      }
      if (tag === 'br') return '\n';
      if (tag === 'ol') {
        return `${Array.from(node.children)
          .filter((child) => child.tagName?.toLowerCase() === 'li')
          .map((child, index) => `${index + 1}. ${Array.from(child.childNodes).map(serializeNode).join('').trim()}`)
          .join('\n')}\n\n`;
      }
      if (tag === 'ul') {
        return `${Array.from(node.children)
          .filter((child) => child.tagName?.toLowerCase() === 'li')
          .map((child) => `- ${Array.from(child.childNodes).map(serializeNode).join('').trim()}`)
          .join('\n')}\n\n`;
      }
      if (['p', 'div', 'section', 'article', 'blockquote', 'pre'].includes(tag)) {
        return `${childText.trim()}\n\n`;
      }
      if (tag === 'li') {
        return `${childText.trim()}\n`;
      }
      return childText;
    };

    return serializeNode(root)
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  return source
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|section|article|blockquote|pre|h[1-6])>/gi, '\n\n')
    .replace(/<(p|div|section|article|blockquote|pre|h[1-6])[^>]*>/gi, '')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/(ul|ol)>/gi, '\n')
    .replace(/<(ul|ol)[^>]*>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const normalizePlainText = (text = '', { trim = true } = {}) => {
  const normalized = `${text ?? ''}`
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
  return trim ? normalized.trim() : normalized;
};

const normalizeRichEditorHtml = (html = '') => {
  const source = `${html || ''}`.replace(/[\u200B-\u200D\uFEFF]/g, '');
  if (typeof document === 'undefined') return source;

  const holder = document.createElement('div');
  holder.innerHTML = source;
  const hasVisibleContent = (element) => (
    Boolean(`${element.textContent || ''}`.replace(/\u00a0/g, ' ').trim()) ||
    Boolean(element.querySelector('img, video, iframe, table, hr, input'))
  );

  holder.querySelectorAll('li').forEach((item) => {
    if (!hasVisibleContent(item)) item.remove();
  });
  holder.querySelectorAll('ul, ol').forEach((list) => {
    if (!list.querySelector('li')) list.remove();
  });

  holder.querySelectorAll('div').forEach((block) => {
    const children = Array.from(block.children);
    if (children.length !== 2 || !children.every((child) => child.tagName === 'SPAN')) return;
    const marker = `${children[0].textContent || ''}`.trim();
    const isListMarker = marker === '•' || marker === '\u2022' || /^\d+\.$/.test(marker);
    if (isListMarker && !hasVisibleContent(children[1])) block.remove();
  });

  Array.from(holder.querySelectorAll('p, div')).reverse().forEach((block) => {
    if (!hasVisibleContent(block) && !block.querySelector('li')) block.remove();
  });

  return holder.innerHTML.trim();
};

const getTextBlockSource = (props = {}) =>
  `${props.fullContent || props.content || htmlToPlainText(props.contentHtml || '') || ''}`;

const getTextBlockPlainText = (props = {}) =>
  `${props.content || htmlToPlainText(props.contentHtml || '') || ''}`;

const getTextBlockHtml = (props = {}) => markdownToHtml(getTextBlockPlainText(props));

const youtubeIdFromUrl = (value = '') => {
  const source = `${value || ''}`.trim();
  if (!source) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/
  ];
  for (const pattern of patterns) {
    const match = source.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
};

const renderRichTextHtml = (text = '') => {
  const blocks = `${text || ''}`.replace(/\r\n/g, '\n').split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);
  if (blocks.length === 0) return '<p class="text-slate-400">Add rich content...</p>';

  return blocks.map((block) => {
    const youtubeId = youtubeIdFromUrl(block);
    if (youtubeId && /^https?:\/\/\S+$/.test(block)) {
      return `<div class="my-3 aspect-video overflow-hidden rounded-xl border border-slate-200 bg-slate-100"><iframe src="https://www.youtube.com/embed/${youtubeId}" title="YouTube video" class="h-full w-full" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`;
    }

    const html = markdownToHtml(block)
      .replace(/(?<!["'=])(https?:\/\/[^\s<]+)/g, (url) => {
        const videoId = youtubeIdFromUrl(url);
        if (videoId) {
          return `<a href="${url}" target="_blank" rel="noreferrer" class="font-semibold text-[#4f46e5] underline">YouTube video</a>`;
        }
        return `<a href="${url}" target="_blank" rel="noreferrer" class="font-semibold text-[#4f46e5] underline">${url}</a>`;
      });
    return html;
  }).join('');
};

const getRichContentHtml = (props = {}) => {
  const html = `${props.contentHtml || ''}`.trim();
  if (html && !/<[a-z][\s\S]*>/i.test(html)) return renderRichTextHtml(html);
  if (html) return props.contentHtml;
  if (props.suppressEmptyContent) return '';
  return renderRichTextHtml(props.content || '');
};

const getRichMediaItems = (props = {}) => Array.isArray(props.media) ? props.media : [];

const getRichHtmlTextLength = (html = '') => {
  if (typeof document === 'undefined') return htmlToPlainText(html).length;
  const holder = document.createElement('div');
  holder.innerHTML = html;
  return holder.textContent?.length || 0;
};

const getRichMediaHeight = (item = {}) => {
  const width = Number(item.width) || 640;
  const height = Number(item.height) || (item.type === 'video' ? Math.round(width * 9 / 16) : 220);
  return Math.max(90, Math.min(720, height));
};

const createRichContentProbe = (html, props = {}) => {
  const probe = document.createElement('div');
  probe.className = `rich-content-block ${getTextSizeClass(props.textSize)} leading-6 text-slate-700`;
  probe.style.cssText = [
    'position:absolute',
    'left:-9999px',
    'top:0',
    'visibility:hidden',
    `width:${getComponentRenderWidthPx(props.width || 'full')}px`,
    'box-sizing:border-box',
    'overflow-wrap:anywhere',
    'word-break:break-word'
  ].join(';');
  probe.innerHTML = html;
  document.body.appendChild(probe);
  return probe;
};

const measureRichContentHtmlHeight = (html, props = {}) => {
  const source = `${html || ''}`;
  if (!source.trim()) return 0;
  if (typeof document === 'undefined') {
    return estimateTextHeight(htmlToPlainText(source), props.width || 'full', props.textSize || 'base', 24);
  }

  const probe = createRichContentProbe(source, props);
  try {
    return Math.ceil(probe.getBoundingClientRect().height);
  } finally {
    document.body.removeChild(probe);
  }
};

const measureRichContentHeight = (props = {}) => {
  const html = getRichContentHtml(props);
  const textHeight = props.suppressEmptyContent && !html.trim()
    ? 0
    : Math.max(72, measureRichContentHtmlHeight(html, props));
  const mediaHeight = getRichMediaItems(props)
    .reduce((total, item) => total + getRichMediaHeight(item) + 24, 0);
  return textHeight + mediaHeight;
};

const getTextBoundary = (root, targetOffset) => {
  const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT);
  let consumed = 0;
  let node = walker.nextNode();
  while (node) {
    const next = consumed + node.nodeValue.length;
    if (targetOffset <= next) {
      return { node, offset: Math.max(0, targetOffset - consumed) };
    }
    consumed = next;
    node = walker.nextNode();
  }
  return { node: root, offset: root.childNodes.length };
};

const splitRichHtmlAtTextOffset = (html, offset) => {
  const source = document.createElement('div');
  source.innerHTML = html;
  const boundary = getTextBoundary(source, offset);
  const firstRange = document.createRange();
  firstRange.selectNodeContents(source);
  firstRange.setEnd(boundary.node, boundary.offset);
  const restRange = document.createRange();
  restRange.selectNodeContents(source);
  restRange.setStart(boundary.node, boundary.offset);

  const serialize = (fragment) => {
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);
    return wrapper.innerHTML;
  };

  return {
    firstHtml: serialize(firstRange.cloneContents()),
    restHtml: serialize(restRange.cloneContents())
  };
};

const splitRichHtmlToFitHeight = (html, availableHeight, props = {}) => {
  const source = `${html || ''}`;
  if (!source.trim()) return { firstHtml: '', restHtml: '' };
  const fullHeight = measureRichContentHtmlHeight(source, props);
  if (fullHeight <= availableHeight) return { firstHtml: source, restHtml: '' };
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { firstHtml: '', restHtml: source };
  }

  const holder = document.createElement('div');
  holder.innerHTML = source;
  const textLength = holder.textContent?.length || 0;
  if (textLength < 2) return { firstHtml: '', restHtml: source };

  let low = 1;
  let high = textLength - 1;
  let best = null;
  while (low <= high) {
    const midpoint = Math.floor((low + high) / 2);
    const candidate = splitRichHtmlAtTextOffset(source, midpoint);
    const candidateHeight = Math.max(72, measureRichContentHtmlHeight(candidate.firstHtml, props));
    if (candidate.firstHtml.trim() && candidate.restHtml.trim() && candidateHeight <= availableHeight) {
      best = candidate;
      low = midpoint + 1;
    } else {
      high = midpoint - 1;
    }
  }

  return best || { firstHtml: '', restHtml: source };
};

const replaceRichHtmlTextRange = (html, start, end, replacementHtml) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return replacementHtml;
  const source = document.createElement('div');
  source.innerHTML = html;
  const startBoundary = getTextBoundary(source, start);
  const endBoundary = getTextBoundary(source, end);
  const beforeRange = document.createRange();
  beforeRange.selectNodeContents(source);
  beforeRange.setEnd(startBoundary.node, startBoundary.offset);
  const afterRange = document.createRange();
  afterRange.selectNodeContents(source);
  afterRange.setStart(endBoundary.node, endBoundary.offset);

  const wrapper = document.createElement('div');
  wrapper.appendChild(beforeRange.cloneContents());
  const replacement = document.createElement('div');
  replacement.innerHTML = replacementHtml;
  while (replacement.firstChild) wrapper.appendChild(replacement.firstChild);
  wrapper.appendChild(afterRange.cloneContents());
  return wrapper.innerHTML;
};

const getCaretOffsetInElement = (root, selectionRange) => {
  if (!root || !selectionRange) return 0;
  const beforeRange = selectionRange.cloneRange();
  beforeRange.selectNodeContents(root);
  beforeRange.setEnd(selectionRange.startContainer, selectionRange.startOffset);
  return beforeRange.toString().length;
};

const setCaretOffsetInElement = (root, targetOffset = 0) => {
  if (!root || typeof window === 'undefined') return false;

  const selection = window.getSelection?.();
  const range = document.createRange?.();
  if (!selection || !range) return false;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  let remaining = Math.max(0, targetOffset);
  let lastNode = null;

  while (node) {
    lastNode = node;
    const length = node.textContent?.length || 0;
    if (remaining <= length) {
      range.setStart(node, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    }
    remaining -= length;
    node = walker.nextNode();
  }

  if (lastNode) {
    const length = lastNode.textContent?.length || 0;
    range.setStart(lastNode, length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  }

  range.selectNodeContents(root);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

const TextBlockEditor = ({
  component,
  onUpdate,
  onBoundaryAction,
  isSelected,
  isPreviewMode,
  allowInlineEditing,
  className,
  style,
  dataFragmentStart,
  pendingCaretOffset = null
}) => {
  const props = component.props || {};
  const editorRef = useRef(null);
  const htmlValue = getTextBlockHtml(props);
  const placeholder = '<p class="mb-0 text-slate-400">Click here to edit this text block directly on the page.</p>';
  const editingEnabled = !isPreviewMode && isSelected && allowInlineEditing;
  const lastHtmlRef = useRef(htmlValue || placeholder);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editingEnabled && document.activeElement === editorRef.current) return;

    const nextHtml = htmlValue || placeholder;
    lastHtmlRef.current = nextHtml;
    if (editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
      if (document.activeElement === editorRef.current) {
        const selection = window.getSelection?.();
        const range = document.createRange?.();
        if (selection && range) {
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
  }, [editingEnabled, htmlValue, placeholder]);

  useEffect(() => {
    if (!editingEnabled || !editorRef.current || !Number.isFinite(pendingCaretOffset)) return;
    editorRef.current.focus({ preventScroll: true });
    setCaretOffsetInElement(editorRef.current, pendingCaretOffset);
  }, [editingEnabled, pendingCaretOffset]);

  return (
    <div
      ref={editorRef}
      contentEditable={editingEnabled}
      suppressContentEditableWarning
      data-text-fragment-editor="true"
      data-component-id={component.id}
      data-fragment-start={dataFragmentStart}
      onPaste={(e) => {
        e.preventDefault();
        const clipboard = e.clipboardData;
        const text = normalizePlainText(
          clipboard?.getData('text/plain') || htmlToPlainText(clipboard?.getData('text/html') || '')
        );
        if (!text) return;
        document.execCommand('insertText', false, text);
      }}
      onKeyDown={(e) => {
        if (!editingEnabled || !props.isSplitFragment || !onBoundaryAction) return;
        if (!['Enter', 'Backspace'].includes(e.key)) return;

        const selection = window.getSelection?.();
        if (!selection?.rangeCount) return;
        const range = selection.getRangeAt(0);
        if (!range.collapsed || !editorRef.current?.contains(range.startContainer)) return;

        const caretOffset = getCaretOffsetInElement(editorRef.current, range);
        const currentText = normalizePlainText(htmlToPlainText(editorRef.current.innerHTML), { trim: false });

        if (e.key === 'Enter' && caretOffset >= currentText.length) {
          e.preventDefault();
          onBoundaryAction({
            componentId: component.id,
            direction: 'next',
            absoluteOffset: Number(props.fragmentEnd) || 0
          });
          return;
        }

        if (e.key === 'Backspace' && caretOffset <= 0) {
          e.preventDefault();
          onBoundaryAction({
            componentId: component.id,
            direction: 'previous',
            absoluteOffset: Number(props.fragmentStart) || 0
          });
        }
      }}
      onInput={(e) => {
        const nextHtml = e.currentTarget.innerHTML;
        lastHtmlRef.current = nextHtml;
        const nextText = normalizePlainText(htmlToPlainText(nextHtml), { trim: false });

        if (props.isSplitFragment && Number.isFinite(props.fragmentStart) && Number.isFinite(props.fragmentEnd)) {
          const source = getTextBlockSource(props);
          const nextFullText = `${source.slice(0, props.fragmentStart)}${nextText}${source.slice(props.fragmentEnd)}`;
          onUpdate(component.id, {
            content: nextFullText,
            contentHtml: markdownToHtml(nextFullText)
          });
          return;
        }

        onUpdate(component.id, {
          contentHtml: markdownToHtml(nextText),
          content: nextText
        });
      }}
      className={`flow-root box-border pt-px ${className || ''}`.trim()}
      style={style}
      data-wysiwyg-active={editingEnabled ? 'true' : 'false'}
    />
  );
};

const parseEmailList = (raw = '') => {
  const normalized = raw
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .filter((item, index, all) => all.indexOf(item) === index);
  return normalized.filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const ResizableImage = ({ component, onUpdate, canResize }) => {
  const props = component.props || {};
  const containerRef = useRef(null);
  const [resizeState, setResizeState] = useState(null);

  useEffect(() => {
    if (!resizeState) return undefined;

    const onMouseMove = (event) => {
      const { corner, startX, startY, startWidth, aspect } = resizeState;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      const signX = corner.includes('left') ? -1 : 1;
      const signY = corner.includes('top') ? -1 : 1;
      const deltaFromX = signX * dx;
      const deltaFromY = signY * dy;
      const delta = Math.abs(deltaFromX) > Math.abs(deltaFromY) ? deltaFromX : deltaFromY;

      const nextWidth = Math.max(120, Math.min(760, startWidth + delta));
      const nextHeight = Math.max(80, nextWidth / aspect);

      onUpdate(component.id, {
        imgWidth: Math.round(nextWidth),
        imgHeight: Math.round(nextHeight)
      });
    };

    const onMouseUp = () => setResizeState(null);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [component.id, onUpdate, resizeState]);

  const startResize = (corner, event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startWidth = Number(props.imgWidth) || rect.width || 320;
    const startHeight = Number(props.imgHeight) || rect.height || 180;
    const aspect = startWidth / startHeight || 1;

    setResizeState({
      corner,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      aspect
    });
  };

  const width = Number(props.imgWidth) || 720;
  const height = Number(props.imgHeight) || undefined;
  const cropScale = Math.max(1, Math.min(3, Number(props.cropScale) || 1));
  const cropX = Math.max(0, Math.min(100, Number(props.cropX ?? 50)));
  const cropY = Math.max(0, Math.min(100, Number(props.cropY ?? 50)));
  const cropEnabled = Boolean(height);

  return (
    <div className="relative inline-block max-w-full" ref={containerRef} style={{ width: `${Math.min(width, 760)}px`, maxWidth: '100%' }}>
      <div className="relative w-full overflow-hidden" style={{ height: height ? `${height}px` : 'auto' }}>
        <img
          src={props.src}
          alt={props.alt || 'Document image'}
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          loading="eager"
          draggable={false}
          className={`block w-full select-none ${cropEnabled ? 'h-full max-w-none object-cover' : 'h-auto object-contain'}`}
          style={
            cropEnabled
              ? {
                  transform: `scale(${cropScale})`,
                  transformOrigin: `${cropX}% ${cropY}%`
                }
              : { height: 'auto' }
          }
        />
      </div>

      {canResize && (
        <>
          <span
            onMouseDown={(e) => startResize('top-left', e)}
            className="absolute -left-2 -top-2 h-4 w-4 cursor-nwse-resize rounded-full border border-[#232753] bg-[#4f46e5]"
          />
          <span
            onMouseDown={(e) => startResize('top-right', e)}
            className="absolute -right-2 -top-2 h-4 w-4 cursor-nesw-resize rounded-full border border-[#232753] bg-[#4f46e5]"
          />
          <span
            onMouseDown={(e) => startResize('bottom-left', e)}
            className="absolute -bottom-2 -left-2 h-4 w-4 cursor-nesw-resize rounded-full border border-[#232753] bg-[#4f46e5]"
          />
          <span
            onMouseDown={(e) => startResize('bottom-right', e)}
            className="absolute -bottom-2 -right-2 h-4 w-4 cursor-nwse-resize rounded-full border border-[#232753] bg-[#4f46e5]"
          />
        </>
      )}
    </div>
  );
};

const RichMedia = ({ item, isEditable = false, onResize }) => {
  const [resizeState, setResizeState] = useState(null);
  const width = Math.max(120, Math.min(760, Number(item.width) || 640));
  const height = getRichMediaHeight(item);
  const alignClass = item.alignment === 'center' ? 'mx-auto' : item.alignment === 'right' ? 'ml-auto' : 'mr-auto';

  useEffect(() => {
    if (!resizeState) return undefined;
    const onMouseMove = (event) => {
      const dx = event.clientX - resizeState.startX;
      const nextWidth = Math.max(120, Math.min(760, resizeState.startWidth + dx));
      const nextHeight = Math.max(90, Math.round(nextWidth / resizeState.aspect));
      onResize?.({ width: Math.round(nextWidth), height: nextHeight });
    };
    const onMouseUp = () => setResizeState(null);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onResize, resizeState]);

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setResizeState({
      startX: event.clientX,
      startWidth: width,
      aspect: width / height || 16 / 9
    });
  };

  return (
    <div className={`relative my-3 max-w-full ${alignClass}`} style={{ width: `${width}px` }}>
      {item.type === 'video' ? (
        <video
          src={item.src}
          controls
          className="block w-full rounded-xl border border-slate-200 bg-black"
          style={{ height: `${height}px` }}
        />
      ) : (
        <img
          src={item.src}
          alt={item.alt || 'Rich content media'}
          className="block w-full rounded-xl border border-slate-200 object-contain"
          style={{ height: `${height}px` }}
          loading="eager"
          draggable={false}
        />
      )}
      {isEditable && (
        <span
          onMouseDown={startResize}
          className="absolute -bottom-2 -right-2 h-4 w-4 cursor-nwse-resize rounded-full border border-[#232753] bg-[#4f46e5]"
        />
      )}
    </div>
  );
};

const RichContentBlock = ({
  component,
  onUpdate,
  onUploadMedia,
  isSelected,
  isPreviewMode,
  allowInternalOverflow = false,
  maxContentHeight = null
}) => {
  const props = component.props || {};
  const alignClass = props.alignment === 'center' ? 'text-center' : props.alignment === 'right' ? 'text-right' : 'text-left';
  const textSizeClass = getTextSizeClass(props.textSize);
  const media = getRichMediaItems(props);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const editorDirtyRef = useRef(false);
  const editingEnabled = isSelected && !isPreviewMode;
  const htmlValue = getRichContentHtml(props);
  const showTextEditor = Boolean(htmlValue.trim()) || !props.suppressEmptyContent;

  useEffect(() => {
    if (!editorRef.current || document.activeElement === editorRef.current) return;
    if (editorRef.current.innerHTML !== htmlValue) {
      editorRef.current.innerHTML = htmlValue;
    }
    editorDirtyRef.current = false;
  }, [htmlValue]);

  const markEditorDirty = () => {
    editorDirtyRef.current = true;
  };

  const commitEditorChanges = () => {
    if (!editorRef.current || !editorDirtyRef.current) return;
    const nextHtml = normalizeRichEditorHtml(editorRef.current.innerHTML);
    editorDirtyRef.current = false;
    if (editorRef.current.innerHTML !== nextHtml) editorRef.current.innerHTML = nextHtml;
    if (nextHtml === normalizeRichEditorHtml(htmlValue)) return;
    onUpdate(component.id, {
      contentHtml: nextHtml,
      content: normalizePlainText(htmlToPlainText(nextHtml), { trim: false })
    });
  };

  const runFormatCommand = (command, value = null) => {
    if (!editingEnabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    markEditorDirty();
  };

  return (
    <div
      className={`rich-content-block ${textSizeClass} leading-6 text-slate-700 ${alignClass} ${
        allowInternalOverflow && !isPreviewMode ? 'overflow-y-auto overscroll-contain pr-2' : ''
      }`}
      style={allowInternalOverflow && !isPreviewMode && maxContentHeight
        ? { maxHeight: `${maxContentHeight}px` }
        : undefined}
    >
      {editingEnabled && showTextEditor && (
        <div
          className="sticky top-0 z-10 mb-3 flex flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
          onMouseDown={(e) => e.preventDefault()}
        >
          <button type="button" onClick={() => runFormatCommand('bold')} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold text-slate-700 hover:border-[#4f46e5]">B</button>
          <button type="button" onClick={() => runFormatCommand('italic')} className="rounded-lg border border-slate-200 px-2 py-1 text-xs italic text-slate-700 hover:border-[#4f46e5]">I</button>
          <button type="button" onClick={() => runFormatCommand('underline')} className="rounded-lg border border-slate-200 px-2 py-1 text-xs underline text-slate-700 hover:border-[#4f46e5]">U</button>
          <button type="button" onClick={() => runFormatCommand('insertUnorderedList')} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]">List</button>
          <button
            type="button"
            onClick={() => {
              if (!editorRef.current) return;
              editorRef.current.innerHTML = renderRichTextHtml(htmlToPlainText(editorRef.current.innerHTML));
              markEditorDirty();
            }}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]"
          >
            Markdown
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.prompt('Link URL');
              if (url) runFormatCommand('createLink', url);
            }}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]"
          >
            Link
          </button>
          <button
            type="button"
            onClick={() => runFormatCommand('justifyLeft')}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]"
          >
            Left
          </button>
          <button
            type="button"
            onClick={() => runFormatCommand('justifyCenter')}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]"
          >
            Center
          </button>
          <button
            type="button"
            onClick={() => runFormatCommand('justifyRight')}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]"
          >
            Right
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-[#4f46e5]"
          >
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => onUploadMedia?.(component.id, e.target.files?.[0])}
          />
        </div>
      )}
      {showTextEditor && (
        <div
          ref={editorRef}
          data-rich-content-editor="true"
          contentEditable={editingEnabled}
          suppressContentEditableWarning
          className={`min-h-[72px] rounded-lg focus:outline-none ${editingEnabled ? 'border border-dashed border-slate-300 bg-white p-3 focus:border-[#4f46e5]' : ''}`}
          onInput={markEditorDirty}
          onBlur={commitEditorChanges}
          onPaste={(e) => {
          if (!editingEnabled) return;
          const imageFile = Array.from(e.clipboardData?.items || [])
            .map((item) => item.kind === 'file' ? item.getAsFile() : null)
            .find((file) => file?.type?.startsWith('image/'));
          if (imageFile) {
            e.preventDefault();
            onUploadMedia?.(component.id, imageFile);
            return;
          }

          const html = e.clipboardData?.getData('text/html');
          const text = e.clipboardData?.getData('text/plain');
          if (!html && !text) return;
          e.preventDefault();
           const nextHtml = html || renderRichTextHtml(text);
           document.execCommand('insertHTML', false, nextHtml);
           markEditorDirty();
           commitEditorChanges();
          }}
        />
      )}
      {media.map((item, index) => (
        <RichMedia
          key={item.id || `${component.id}-media-${index}`}
          item={item}
          isEditable={isSelected && !isPreviewMode}
          onResize={(updates) => {
            const nextMedia = media.map((mediaItem, mediaIndex) => (
              mediaIndex === index ? { ...mediaItem, ...updates } : mediaItem
            ));
            onUpdate(component.id, { media: nextMedia });
          }}
        />
      ))}
    </div>
  );
};

const RenderComponent = ({
  component,
  onUpdate,
  onUploadRichMedia,
  onBoundaryAction,
  isSelected,
  isPreviewMode,
  allowInternalOverflow = false,
  maxContentHeight = null,
  pendingCaretOffset = null
}) => {
  const props = component.props || {};
  const alignClass = props.alignment === 'center' ? 'text-center' : props.alignment === 'right' ? 'text-right' : 'text-left';
  const justifyClass = props.alignment === 'center' ? 'justify-center' : props.alignment === 'right' ? 'justify-end' : 'justify-start';
  const textSizeClass = getTextSizeClass(props.textSize);
  const showOverflowEditorUi = allowInternalOverflow && !isPreviewMode;

  if (component.type === 'heading') {
    const level = props.level || 2;
    const levelSize = level === 1 ? 'text-3xl' : level === 2 ? 'text-2xl' : 'text-xl';
    const size = props.textSize ? textSizeClass : levelSize;
    return <h3 className={`font-bold ${size} ${alignClass} text-[#232753] mb-3`}>{props.content}</h3>;
  }

  if (component.type === 'textBlock') {
    return (
      <div
        className="relative"
        onMouseDown={isPreviewMode ? (e) => e.stopPropagation() : undefined}
        onClick={isPreviewMode ? (e) => e.stopPropagation() : undefined}
      >
        <TextBlockEditor
          component={component}
          onUpdate={onUpdate}
          onBoundaryAction={onBoundaryAction}
          isSelected={isSelected}
          isPreviewMode={isPreviewMode}
          allowInlineEditing
          dataFragmentStart={Number.isFinite(component?.props?.fragmentStart) ? component.props.fragmentStart : 0}
          pendingCaretOffset={pendingCaretOffset}
          className={`min-h-[56px] rounded-lg ${textSizeClass} leading-6 text-slate-700 ${alignClass} focus:outline-none ${
            isPreviewMode ? 'cursor-text select-text' : ''
          } ${
            showOverflowEditorUi ? 'overflow-y-auto overscroll-contain pr-2' : ''
          }`}
          style={showOverflowEditorUi && maxContentHeight
            ? { maxHeight: `${maxContentHeight}px` }
            : undefined}
        />
        {showOverflowEditorUi && (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Content exceeds one page. Scroll here, then cut the extra text and paste it into a block on the next page.
          </div>
        )}
      </div>
    );
  }

  if (component.type === 'richContent') {
    return (
      <RichContentBlock
        component={component}
        onUpdate={onUpdate}
        onUploadMedia={onUploadRichMedia}
        isSelected={isSelected}
        isPreviewMode={isPreviewMode}
        allowInternalOverflow={allowInternalOverflow}
        maxContentHeight={maxContentHeight}
      />
    );
  }

  if (component.type === 'sectionDivider') {
    const lineStyleMap = {
      solid: 'solid',
      dashed: 'dashed',
      dotted: 'dotted'
    };
    const showLabel = props.showLabel !== false && props.content;
    const thickness = Math.max(1, Math.min(6, Number(props.lineThickness ?? 1)));
    const spacingTop = Math.max(0, Math.min(80, Number(props.spacingTop ?? 24)));
    const spacingBottom = Math.max(0, Math.min(80, Number(props.spacingBottom ?? 24)));
    const labelClass = props.alignment === 'center' ? 'mx-auto' : props.alignment === 'right' ? 'ml-auto' : 'mr-auto';

    return (
      <div className="w-full" style={{ paddingTop: `${spacingTop}px`, paddingBottom: `${spacingBottom}px` }}>
        {showLabel && (
          <div className={`mb-3 w-fit rounded-full bg-white px-3 py-1 font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm ${labelClass} ${textSizeClass}`}>
            {props.content}
          </div>
        )}
        <div
          className="w-full"
          style={{
            borderTopWidth: `${thickness}px`,
            borderTopStyle: lineStyleMap[props.lineStyle] || 'solid',
            borderTopColor: props.lineColor || '#cbd5e1'
          }}
        />
      </div>
    );
  }

  if (component.type === 'navigationMenu') {
    const items = Array.isArray(props.items) ? props.items : [];
    return (
      <nav className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-[#232753]">
          <ListTree className="h-4 w-4 text-[#4f46e5]" />
          <span>{props.title || 'Document Navigation'}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">Add headings or section dividers to populate the navigation menu.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {items.map((item) => (
              <a
                key={`${component.id}-${item.id}`}
                href={`#smart-section-${item.id}`}
                onClick={isPreviewMode ? undefined : (e) => e.preventDefault()}
                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#232753] hover:border-[#4f46e5] hover:bg-[#eef2ff]"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#eef2ff] text-xs text-[#4f46e5]">
                  {item.order}
                </span>
                <span className="min-w-0 truncate">{item.label}</span>
              </a>
            ))}
          </div>
        )}
      </nav>
    );
  }

  if (component.type === 'pageBreak') {
    if (isPreviewMode) return null;
    return (
      <div className="py-2">
        <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
          <span className="h-px flex-1 bg-slate-200" />
          <span>Page Break</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
      </div>
    );
  }

  if (component.type === 'image') {
    return (
      <div className={`mb-0 flex ${justifyClass}`}>
        <ResizableImage component={component} onUpdate={onUpdate} canResize={isSelected && !isPreviewMode} />
      </div>
    );
  }

  if (component.type === 'textInput') {
    return (
      <label className="block mb-3">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <input
          type="text"
          value={props.value || ''}
          placeholder={props.placeholder || ''}
          onChange={(e) => onUpdate(component.id, { value: e.target.value })}
          className={`w-full rounded-xl border border-slate-200 px-3 py-2 ${textSizeClass} focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30`}
        />
      </label>
    );
  }

  if (component.type === 'textareaInput') {
    return (
      <label className="block mb-3">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <textarea
          value={props.value || ''}
          rows={props.rows || 3}
          placeholder={props.placeholder || ''}
          onChange={(e) => onUpdate(component.id, { value: e.target.value })}
          className={`w-full rounded-xl border border-slate-200 px-3 py-2 ${textSizeClass} focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30`}
        />
      </label>
    );
  }

  if (component.type === 'dropdown') {
    return (
      <label className="block mb-3">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <select
          value={props.value || ''}
          onChange={(e) => onUpdate(component.id, { value: e.target.value })}
          className={`w-full rounded-xl border border-slate-200 px-3 py-2 ${textSizeClass} focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30`}
        >
          <option value="">Select an option</option>
          {(props.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </label>
    );
  }

  if (component.type === 'radio') {
    return (
      <fieldset className="mb-3">
        <legend className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</legend>
        <div className="space-y-1">
          {(props.options || []).map((opt) => (
            <label key={`${component.id}-radio-${opt}`} className={`flex items-center gap-2 text-[#232753] ${textSizeClass}`}>
              <input
                type="radio"
                name={`studio-radio-${component.id}`}
                checked={(props.value || '') === opt}
                onChange={() => onUpdate(component.id, { value: opt })}
                className="h-4 w-4 border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (component.type === 'checklist') {
    const selectedValues = Array.isArray(props.value) ? props.value : [];
    const toggleValue = (option) => {
      if (selectedValues.includes(option)) {
        onUpdate(component.id, { value: selectedValues.filter((item) => item !== option) });
      } else {
        onUpdate(component.id, { value: [...selectedValues, option] });
      }
    };

    return (
      <fieldset className="mb-3">
        <legend className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</legend>
        <div className="space-y-1">
          {(props.options || []).map((opt) => (
            <label key={`${component.id}-checklist-${opt}`} className={`flex items-center gap-2 text-[#232753] ${textSizeClass}`}>
              <input
                type="checkbox"
                checked={selectedValues.includes(opt)}
                onChange={() => toggleValue(opt)}
                className="h-4 w-4 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
              />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (component.type === 'checkbox') {
    return (
      <label className={`mb-3 flex items-center gap-2 font-medium text-[#232753] ${textSizeClass} ${alignClass}`}>
        <input
          type="checkbox"
          checked={props.value === 'true'}
          onChange={(e) => onUpdate(component.id, { value: e.target.checked ? 'true' : 'false' })}
          className="h-4 w-4 rounded border-slate-300 text-[#4f46e5] focus:ring-[#4f46e5]"
        />
        <span>{props.label}</span>
      </label>
    );
  }

  if (component.type === 'datePicker') {
    return (
      <label className="block mb-3">
        <span className={`mb-1 block font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label}</span>
        <input
          type="date"
          value={props.value || ''}
          onChange={(e) => onUpdate(component.id, { value: e.target.value })}
          className={`w-full rounded-xl border border-slate-200 px-3 py-2 ${textSizeClass} focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30`}
        />
      </label>
    );
  }

  if (['barcodeInput', 'qrCodeInput', 'nfcInput'].includes(component.type)) {
    return (
      <SmartSensorField
        type={component.type}
        fieldKey={component?.props?.variableName || component.id}
        value={props.value || ''}
        label={props.label}
        placeholder={props.placeholder || ''}
        textSizeClass={textSizeClass}
        alignClass={alignClass}
        onValueChange={(nextValue) => onUpdate(component.id, { value: nextValue })}
      />
    );
  }

  if (component.type === 'progress') {
    const progressValue = Math.max(0, Math.min(100, Number(props.progressValue ?? 0)));
    const progressColor = props.progressColor || '#4f46e5';
    return (
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between">
          <span className={`${textSizeClass} font-semibold text-[#232753]`}>{props.label || 'Progress'}</span>
          <span className="text-xs font-semibold text-slate-500">{progressValue}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full transition-all" style={{ width: `${progressValue}%`, backgroundColor: progressColor }} />
        </div>
      </div>
    );
  }

  if (component.type === 'rating') {
    const max = Math.max(1, Math.min(10, Number(props.maxRating ?? 5)));
    const current = Math.max(0, Math.min(max, Number(props.value ?? 0)));
    return (
      <div className="mb-3">
        <div className={`mb-1 font-semibold text-[#232753] ${textSizeClass}`}>{props.label || 'Rating'}</div>
        <div className="flex items-center gap-1">
          {Array.from({ length: max }).map((_, idx) => {
            const active = idx < current;
            return (
              <button
                key={`${component.id}-star-${idx}`}
                type="button"
                onClick={() => onUpdate(component.id, { value: String(idx + 1) })}
                className={`text-[2.5rem] leading-none ${active ? 'text-[#fbbf24]' : 'text-slate-300'}`}
              >
                ★
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (component.type === 'trafficLightStatus') {
    const current = ['red', 'orange', 'green'].includes(props.value) ? props.value : 'orange';
    const statusButtons = [
      { value: 'red', label: 'R', activeClass: 'bg-rose-500 text-white border-rose-500', idleClass: 'border-rose-200 bg-rose-50 text-rose-500' },
      { value: 'orange', label: 'O', activeClass: 'bg-amber-500 text-white border-amber-500', idleClass: 'border-amber-200 bg-amber-50 text-amber-500' },
      { value: 'green', label: 'G', activeClass: 'bg-emerald-500 text-white border-emerald-500', idleClass: 'border-emerald-200 bg-emerald-50 text-emerald-500' }
    ];

    return (
      <div className="mb-3">
        <div className={`mb-1.5 font-semibold text-[#232753] ${alignClass} ${textSizeClass}`}>{props.label || 'Status'}</div>
        <div className={`flex flex-wrap items-center gap-2 ${justifyClass}`}>
          {statusButtons.map((status) => {
            const active = current === status.value;
            return (
              <button
                key={`${component.id}-traffic-${status.value}`}
                type="button"
                onClick={() => onUpdate(component.id, { value: status.value })}
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-[1.05rem] font-bold leading-none transition ${
                  active ? status.activeClass : status.idleClass
                }`}
                title={status.value}
              >
                {status.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (component.type === GROUP_COMPONENT_TYPE) {
    const children = component.children || [];
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Grouped Block</div>
            <div className="mt-1 text-sm font-semibold text-[#232753]">{props.label || `Grouped Block (${children.length})`}</div>
          </div>
          <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm">
            {flattenComponents(children).length} items
          </div>
        </div>
        <div className="pointer-events-none grid grid-cols-12 gap-2 opacity-95">
          {children.map((child, index) => (
            <div key={`${component.id}-group-child-${child.id || index}`} className={getWidthClass(child?.props?.width)}>
              <RenderComponent
                component={child}
                onUpdate={() => {}}
                onBoundaryAction={() => {}}
                isSelected={false}
                isPreviewMode
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (component.type === 'submitButton') {
    return (
      <div className={`flex pt-2 ${justifyClass}`}>
        <button type="button" className={`rounded-xl bg-[#232753] px-4 py-2 font-semibold text-white hover:bg-[#1b1f47] ${textSizeClass}`}>
          {props.buttonText || 'Submit'}
        </button>
      </div>
    );
  }

  return null;
};

const SmartChipModal = ({ config, initialData, onSave, onClose }) => {
  const [text, setText] = useState(initialData?.text || '');
  const [rating, setRating] = useState(Number(initialData?.rating || 0));
  const [progressValue, setProgressValue] = useState(Number(initialData?.progress || 0));
  const [status, setStatus] = useState(['red', 'orange', 'green'].includes(initialData?.status) ? initialData.status : 'orange');

  return (
    <div
      className="absolute right-0 top-full z-50 mt-3 w-80 rounded-xl border border-slate-200 bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h4 className="text-sm font-bold text-[#232753]">Smart Chip</h4>
        <button onClick={onClose} className="text-xs font-semibold text-slate-500 hover:text-[#232753]">Close</button>
      </div>
      <div className="space-y-4 p-4">
        {config.enableText !== false && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</label>
            <textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
        )}
        {config.enableRating && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Rating</label>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, index) => {
                const active = index < rating;
                return (
                  <button
                    key={`smart-chip-rating-${index}`}
                    type="button"
                    onClick={() => setRating(index + 1)}
                    className={`text-[2.5rem] leading-none ${active ? 'text-[#fbbf24]' : 'text-slate-300'}`}
                  >
                    *
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {config.enableProgress && (
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Progress</label>
              <span className="text-xs font-semibold text-[#232753]">{progressValue}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={progressValue}
              onChange={(e) => setProgressValue(Number(e.target.value))}
              className="w-full accent-[#4f46e5]"
            />
          </div>
        )}
        {config.enableStatus && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
            <div className="flex items-center gap-2">
              {[
                { value: 'red', label: 'R', activeClass: 'bg-rose-500 text-white border-rose-500', idleClass: 'border-rose-200 bg-rose-50 text-rose-500' },
                { value: 'orange', label: 'O', activeClass: 'bg-amber-500 text-white border-amber-500', idleClass: 'border-amber-200 bg-amber-50 text-amber-500' },
                { value: 'green', label: 'G', activeClass: 'bg-emerald-500 text-white border-emerald-500', idleClass: 'border-emerald-200 bg-emerald-50 text-emerald-500' }
              ].map((option) => {
                const active = status === option.value;
                return (
                  <button
                    key={`smart-chip-status-${option.value}`}
                    type="button"
                    onClick={() => setStatus(option.value)}
                    className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold ${
                      active ? option.activeClass : option.idleClass
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3">
        <button onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
        <button
          onClick={() => onSave({ text, rating, progress: progressValue, status })}
          className="rounded-lg bg-[#232753] px-3 py-1.5 text-xs font-semibold text-white"
        >
          Save
        </button>
      </div>
    </div>
  );
};

const SmartProcessingStudio = () => {
  const { id: editFormId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const functions = getFunctions(app);

  const [file, setFile] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [agentPrompt, setAgentPrompt] = useState('');
  const [agentIntent, setAgentIntent] = useState('auto');
  const [agentAction, setAgentAction] = useState('replace');
  const [showAgentBuilder, setShowAgentBuilder] = useState(true);
  const [groupStartId, setGroupStartId] = useState(null);
  const [strategy, setStrategy] = useState('strict');
  const [theme, setTheme] = useState('light');
  const [layoutMode, setLayoutMode] = useState('original');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isOcrExtracting, setIsOcrExtracting] = useState(false);
  const [isAgentBuilding, setIsAgentBuilding] = useState(false);
  const [ocrProgressLabel, setOcrProgressLabel] = useState('');
  const [ocrMode, setOcrMode] = useState('both');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [activeSidebar, setActiveSidebar] = useState('blocks');
  const [components, setComponents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTextFragmentId, setActiveTextFragmentId] = useState(null);
  const [activeSmartChipId, setActiveSmartChipId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [docConfig, setDocConfig] = useState(DEFAULT_DOC_CONFIG);
  const [documentTitle, setDocumentTitle] = useState('');
  const [componentHistory, setComponentHistory] = useState([]);
  const [redoHistory, setRedoHistory] = useState([]);
  const [uploadingImageId, setUploadingImageId] = useState(null);
  const [existingFormMeta, setExistingFormMeta] = useState(null);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [extractedSourcePdf, setExtractedSourcePdf] = useState(null);
  const [shareVisibility, setShareVisibility] = useState('private');
  const [shareEmailsInput, setShareEmailsInput] = useState('');
  const studioRootRef = useRef(null);
  const pendingCaretRequestRef = useRef(null);

  const selected = useMemo(() => components.find((c) => c.id === selectedId) || null, [components, selectedId]);
  const selectedIndex = useMemo(() => components.findIndex((c) => c.id === selectedId), [components, selectedId]);
  const dataBindings = useMemo(() => buildDataBindings(components), [components]);
  const pageContentHeight = useMemo(
    () => getPageContentHeight(Boolean(docConfig.showHeader), Boolean(docConfig.showFooter)),
    [docConfig.showHeader, docConfig.showFooter]
  );
  const pageBodyHeight = useMemo(
    () => getPageBodyHeight(Boolean(docConfig.showHeader), Boolean(docConfig.showFooter)),
    [docConfig.showHeader, docConfig.showFooter]
  );
  const visibleComponents = useMemo(
    () => (isPreviewMode ? filterVisibleComponentTree(components, flattenComponents(components)) : components),
    [components, isPreviewMode]
  );
  const navigationItems = useMemo(() => buildNavigationItems(visibleComponents), [visibleComponents]);
  const showStudioNavigationPanel = Boolean(isPreviewMode && docConfig.showNavigationMenu);
  const paginatedPages = useMemo(
    () => paginateComponents(visibleComponents, {
      includePageBreakMarker: !isPreviewMode,
      pageContentHeight,
      editingRichContentId: !isPreviewMode ? selectedId : null
    }),
    [visibleComponents, isPreviewMode, pageContentHeight, selectedId]
  );

  const ensureUniqueBinding = (candidate, componentId, pool = components) => {
    const base = toBindingKey(candidate);
    if (!base) return '';
    const existing = new Set(
      pool
        .filter((item) => item.id !== componentId)
        .map((item) => toBindingKey(item?.props?.variableName || ''))
        .filter(Boolean)
    );
    if (!existing.has(base)) return base;

    let cursor = 2;
    let next = `${base}_${cursor}`;
    while (existing.has(next)) {
      cursor += 1;
      next = `${base}_${cursor}`;
    }
    return next;
  };

  const normalizeComponentBindings = (incoming, existingPool = components) => {
    const existingFlat = flattenComponents(existingPool);
    const normalizedFlat = [];

    const normalizeOne = (component, index) => {
      const next = withSmartChipDefaults(component);

      if (isGroupComponent(next)) {
        const normalizedChildren = (next.children || []).map((child, childIndex) => normalizeOne(child, childIndex));
        return {
          ...next,
          props: {
            width: 'full',
            label: next?.props?.label || `Grouped Block (${flattenComponents(normalizedChildren).length})`,
            ...(next.props || {})
          },
          children: normalizedChildren
        };
      }

      if (!isBindableComponent(next.type)) {
        normalizedFlat.push(next);
        return next;
      }

      const fallback = buildBindingLabel(next, index);
      const candidate = toBindingKey(next?.props?.variableName || fallback || next.type);
      const unique = ensureUniqueBinding(candidate, next.id, [...existingFlat, ...normalizedFlat]);
      const normalizedLeaf = {
        ...next,
        props: {
          ...(next.props || {}),
          variableName: unique
        }
      };
      normalizedFlat.push(normalizedLeaf);
      return normalizedLeaf;
    };

    return incoming.map((component, index) => normalizeOne(component, index));
  };

  const hydrateGeneratedComponents = (incoming) =>
    incoming.map((component) => ({
      ...component,
      props: {
        ...(DEFAULT_PROPS[component.type] || {}),
        ...(component?.props || {})
      }
    }));

  const stripTransientComponentProps = (props = {}) => {
    const next = { ...(props || {}) };
    delete next.value;
    delete next.smartChipConfig;
    delete next.smartChipData;
    return next;
  };

  const filterAppendOnlyComponents = (incoming, existing) => {
    const existingSignatures = new Set(
      flattenComponents(existing || []).map((component) => JSON.stringify({
        type: component?.type || '',
        props: stripTransientComponentProps(component?.props || {})
      }))
    );

    return (incoming || []).filter((component) => {
      const signature = JSON.stringify({
        type: component?.type || '',
        props: stripTransientComponentProps(component?.props || {})
      });
      return !existingSignatures.has(signature);
    });
  };

  const cloneComponentTree = (component) => ({
    ...component,
    id: id(),
    props: {
      ...(component?.props || {})
    },
    children: isGroupComponent(component)
      ? (component.children || []).map((child) => cloneComponentTree(child))
      : undefined
  });

  const groupComponentRange = (startId, endId) => {
    const startIndex = components.findIndex((component) => component.id === startId);
    const endIndex = components.findIndex((component) => component.id === endId);
    if (startIndex < 0 || endIndex < 0) return;

    const rangeStart = Math.min(startIndex, endIndex);
    const rangeEnd = Math.max(startIndex, endIndex);
    const slice = components.slice(rangeStart, rangeEnd + 1);
    if (slice.length < 2) return;

    const group = {
      id: id(),
      type: GROUP_COMPONENT_TYPE,
      props: {
        label: `Grouped Block (${slice.length})`,
        width: 'full'
      },
      children: slice
    };

    const updated = [
      ...components.slice(0, rangeStart),
      group,
      ...components.slice(rangeEnd + 1)
    ];

    pushHistoryState(updated, group.id);
    setGroupStartId(null);
    setActiveTextFragmentId(null);
    setActiveSmartChipId(null);
    setActiveSidebar('properties');
  };

  const ungroupComponent = (groupId) => {
    const groupIndex = components.findIndex((component) => component.id === groupId && isGroupComponent(component));
    if (groupIndex < 0) return;
    const group = components[groupIndex];
    const updated = [
      ...components.slice(0, groupIndex),
      ...(group.children || []),
      ...components.slice(groupIndex + 1)
    ];
    pushHistoryState(updated, group.children?.[0]?.id || null);
    setActiveTextFragmentId(null);
    setActiveSmartChipId(null);
  };

  const pushHistoryState = (nextComponents, nextSelectedId = selectedId) => {
    setComponentHistory((prev) => [
      ...prev.slice(-49),
      {
        components,
        selectedId
      }
    ]);
    setRedoHistory([]);
    setComponents(nextComponents);
    setSelectedId(nextSelectedId ?? null);
  };

  const replaceComponentsState = (nextComponents, nextSelectedId = null) => {
    setComponentHistory([]);
    setRedoHistory([]);
    setComponents(nextComponents);
    setSelectedId(nextSelectedId);
  };

  const undoLastChange = () => {
    if (!componentHistory.length) return;
    const previousState = componentHistory[componentHistory.length - 1];
    setComponentHistory((prev) => prev.slice(0, -1));
    setRedoHistory((prev) => [
      ...prev.slice(-49),
      {
        components,
        selectedId
      }
    ]);
    setComponents(previousState.components || []);
    setSelectedId(previousState.selectedId || null);
    setActiveTextFragmentId(null);
    setActiveSmartChipId(null);
  };

  const redoLastChange = () => {
    if (!redoHistory.length) return;
    const nextState = redoHistory[redoHistory.length - 1];
    setRedoHistory((prev) => prev.slice(0, -1));
    setComponentHistory((prev) => [
      ...prev.slice(-49),
      {
        components,
        selectedId
      }
    ]);
    setComponents(nextState.components || []);
    setSelectedId(nextState.selectedId || null);
    setActiveTextFragmentId(null);
    setActiveSmartChipId(null);
  };

  const moveComponentByOffset = (compId, offset) => {
    const currentIndex = components.findIndex((component) => component.id === compId);
    if (currentIndex < 0) return;
    const nextIndex = currentIndex + offset;
    if (nextIndex < 0 || nextIndex >= components.length) return;

    const nextComponents = [...components];
    const [moved] = nextComponents.splice(currentIndex, 1);
    nextComponents.splice(nextIndex, 0, moved);
    pushHistoryState(nextComponents, compId);
    setActiveTextFragmentId(null);
  };

  useEffect(() => {
    if (!editFormId) return;
    if (!user?.uid) return;

    let cancelled = false;
    const loadForEdit = async () => {
      setIsLoadingExisting(true);
      try {
        const form = await formService.getFormById(editFormId);
        if (form.userId !== user.uid) {
          toast.error('Only the owner can edit this document.');
          navigate(`/form/${editFormId}`);
          return;
        }

        const incomingComponents = Array.isArray(form?.smartDocument?.components)
          ? form.smartDocument.components
          : [];

        const normalized = normalizeComponentBindings(
          incomingComponents.map((component) => ({
            ...component,
            id: component?.id || id()
          })),
          []
        );

        if (cancelled) return;

        replaceComponentsState(normalized, null);
        setActiveTextFragmentId(null);
        setActiveSmartChipId(null);
        setInstructions(form?.config?.instructions || '');
        setStrategy(form?.config?.strategy || 'strict');
        setTheme(form?.config?.theme || 'light');
        setLayoutMode(form?.layoutMode || 'original');
        setDocumentTitle(form?.title || '');
        setDocConfig({
          ...DEFAULT_DOC_CONFIG,
          ...(form?.smartDocument?.documentConfig || {})
        });
        setExistingFormMeta({
          id: form.id,
          title: form.title || '',
          visibility: form.visibility || 'private',
          allowedEmails: Array.isArray(form.allowedEmails) ? form.allowedEmails : [],
          originalDocumentId: form.originalDocumentId || form.documentId || form.id,
          shareSettings: form.shareSettings || null,
          sourcePdf: form.sourcePdf || null
        });
        setShareVisibility(form.visibility || 'private');
        setShareEmailsInput(Array.isArray(form.allowedEmails) ? form.allowedEmails.join(', ') : '');
        toast.success('Document loaded for editing.');
      } catch (error) {
        console.error('Failed to load form for editing:', error);
        toast.error(error?.message || 'Failed to load document for editing.');
        navigate('/forms');
      } finally {
        if (!cancelled) setIsLoadingExisting(false);
      }
    };

    loadForEdit();
    return () => {
      cancelled = true;
    };
  }, [editFormId, navigate, user?.uid]);

  useEffect(() => {
    const request = pendingCaretRequestRef.current;
    if (!request || typeof document === 'undefined') return;

    const { componentId, absoluteOffset } = request;
    let targetItem = null;

    paginatedPages.some((pageItems) =>
      pageItems.some((item) => {
        if (item.component?.id !== componentId || item.component?.type !== 'textBlock') return false;
        const props = item.renderComponent?.props || {};
        const start = Number.isFinite(props.fragmentStart) ? props.fragmentStart : 0;
        const text = getTextBlockPlainText(props);
        const end = Number.isFinite(props.fragmentEnd) ? props.fragmentEnd : start + text.length;
        if (absoluteOffset < start || absoluteOffset > end) return false;
        targetItem = { start };
        return true;
      })
    );

    if (!targetItem) return;

    const relativeOffset = Math.max(0, absoluteOffset - targetItem.start);
    const selector = `[data-text-fragment-editor="true"][data-component-id="${componentId}"][data-fragment-start="${targetItem.start}"]`;
    const frame = window.requestAnimationFrame(() => {
      const editor = document.querySelector(selector);
      if (!editor) return;
      setActiveTextFragmentId(`${componentId}:${targetItem.start}`);
      editor.focus();
      setCaretOffsetInElement(editor, relativeOffset);
      pendingCaretRequestRef.current = null;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [paginatedPages]);

  useEffect(() => {
    if (isPreviewMode) return undefined;

    const handlePointerDown = (event) => {
      const root = studioRootRef.current;
      if (!root?.contains(event.target)) return;
      if (event.target.closest('[data-studio-component-id]')) return;
      if (event.target.closest('[data-studio-sidebar="true"]')) return;
      const activeEditor = document.activeElement?.closest?.('[data-rich-content-editor="true"]');
      activeEditor?.blur();
      setSelectedId(null);
      setActiveTextFragmentId(null);
      setActiveSmartChipId(null);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isPreviewMode]);

  useEffect(() => {
    if (groupStartId && !components.some((component) => component.id === groupStartId)) {
      setGroupStartId(null);
    }
  }, [components, groupStartId]);

  const updateComponent = (compId, updates) => {
    if (isGroupComponent(selected) && selected?.id === compId) return;
    const nextComponents = components.map((component) => (
      component.id === compId ? { ...component, props: { ...component.props, ...updates } } : component
    ));
    pushHistoryState(nextComponents, compId);
  };

  const updateRichContentFragment = (compId, fragmentProps, updates) => {
    const nextComponents = components.map((component) => {
      if (component.id !== compId || component.type !== 'richContent') return component;
      const nextProps = { ...(component.props || {}) };

      if (Object.prototype.hasOwnProperty.call(updates, 'contentHtml')) {
        const sourceHtml = getRichContentHtml(component.props || {});
        const start = Number(fragmentProps?.fragmentStart) || 0;
        const end = Number.isFinite(fragmentProps?.fragmentEnd) ? fragmentProps.fragmentEnd : start;
        nextProps.contentHtml = replaceRichHtmlTextRange(sourceHtml, start, end, updates.contentHtml || '');
        nextProps.content = normalizePlainText(htmlToPlainText(nextProps.contentHtml), { trim: false });
      }

      if (Array.isArray(updates.media)) {
        const sourceMedia = [...getRichMediaItems(component.props)];
        const fragmentMedia = getRichMediaItems(fragmentProps);
        updates.media.forEach((updatedItem, index) => {
          const originalItem = fragmentMedia[index];
          const sourceIndex = originalItem?.id
            ? sourceMedia.findIndex((item) => item?.id === originalItem.id)
            : sourceMedia.indexOf(originalItem);
          if (sourceIndex >= 0) sourceMedia[sourceIndex] = updatedItem;
        });
        nextProps.media = sourceMedia;
      }

      Object.entries(updates).forEach(([key, value]) => {
        if (!['contentHtml', 'content', 'media'].includes(key)) nextProps[key] = value;
      });
      return { ...component, props: nextProps };
    });
    pushHistoryState(nextComponents, compId);
  };

  const updateTextBlockContent = (compId, transform, options = {}) => {
    let nextSelectionId = selectedId;

    const nextComponents = components.map((component) => {
      if (component.id !== compId || component.type !== 'textBlock') return component;
      const currentSource = getTextBlockSource(component.props || {});
      const nextContent = typeof transform === 'function' ? transform(currentSource, component) : transform;
      const normalizedContent = typeof nextContent === 'string' ? nextContent : currentSource;
      nextSelectionId = compId;
      return {
        ...component,
        props: {
          ...(component.props || {}),
          content: normalizedContent,
          contentHtml: markdownToHtml(normalizedContent)
        }
      };
    });

    pushHistoryState(nextComponents, nextSelectionId);

    if (options.caret) {
      pendingCaretRequestRef.current = {
        componentId: compId,
        absoluteOffset: Math.max(0, options.caret.absoluteOffset || 0)
      };
    }

  };

  const handleTextBoundaryAction = ({ componentId, direction, absoluteOffset }) => {
    if (!componentId || !Number.isFinite(absoluteOffset)) return;

    if (direction === 'next') {
      updateTextBlockContent(
        componentId,
        (source) => `${source.slice(0, absoluteOffset)}\n${source.slice(absoluteOffset)}`,
        { caret: { absoluteOffset: absoluteOffset + 1 } }
      );
      return;
    }

    if (direction === 'previous' && absoluteOffset > 0) {
      updateTextBlockContent(
        componentId,
        (source) => `${source.slice(0, absoluteOffset - 1)}${source.slice(absoluteOffset)}`,
        { caret: { absoluteOffset: absoluteOffset - 1 } }
      );
    }
  };

  const changeComponentType = (compId, nextType) => {
    const index = components.findIndex((item) => item.id === compId);
    if (index < 0) return;
    const current = components[index];
    if (current.type === nextType || isGroupComponent(current)) return;

    const currentProps = current.props || {};
    const nextDefaults = { ...(DEFAULT_PROPS[nextType] || {}) };
    const nextProps = { ...nextDefaults };

      const transferable = [
        'alignment',
        'textSize',
        'width',
        'required',
        'label',
        'placeholder',
        'rows',
        'progressValue',
        'progressColor',
        'maxRating',
        'buttonText',
        'content',
        'contentHtml',
        'src',
        'alt',
        'imgWidth',
        'imgHeight',
        'cropScale',
        'cropX',
        'cropY',
        'smartChipConfig',
        'smartChipData',
        'visibilityLogic'
      ];

    transferable.forEach((key) => {
      if (currentProps[key] !== undefined) {
        nextProps[key] = currentProps[key];
      }
    });

    if (['dropdown', 'radio', 'checklist'].includes(nextType)) {
      const incomingOptions = Array.isArray(currentProps.options)
        ? currentProps.options
        : Array.isArray(nextDefaults.options)
          ? nextDefaults.options
          : ['Option 1', 'Option 2'];
      nextProps.options = incomingOptions;
    }

    if (isBindableComponent(nextType)) {
      const existing = new Set(
        components
          .filter((item) => item.id !== compId && isBindableComponent(item.type))
          .map((item) => toBindingKey(item?.props?.variableName || ''))
          .filter(Boolean)
      );
      const base = toBindingKey(currentProps.variableName || nextDefaults.variableName || buildBindingLabel(current, index) || nextType);
      let unique = base || toBindingKey(nextType) || `field_${index + 1}`;
      let cursor = 2;
      while (existing.has(unique)) {
        unique = `${base || toBindingKey(nextType)}_${cursor}`;
        cursor += 1;
      }
      nextProps.variableName = unique;
    } else {
      delete nextProps.variableName;
    }

    const updated = [...components];
    updated[index] = {
      ...current,
      type: nextType,
      props: nextProps
    };
    pushHistoryState(updated, compId);
  };

  const addComponent = (type) => {
    const next = normalizeComponentBindings([{ id: id(), type, props: { ...(DEFAULT_PROPS[type] || {}) } }])[0];
    let updated = [...components, next];
    if (selectedId) {
      const index = components.findIndex((item) => item.id === selectedId);
      if (index >= 0) {
        updated = [...components];
        updated.splice(index + 1, 0, next);
      }
    }
    pushHistoryState(updated, next.id);
    setActiveTextFragmentId(null);
    setActiveSidebar('properties');
  };

  const addPageBreak = () => {
    const next = { id: id(), type: 'pageBreak', props: { ...(DEFAULT_PROPS.pageBreak || {}) } };
    let updated = [...components, next];
    if (selectedId) {
      const index = components.findIndex((item) => item.id === selectedId);
      if (index >= 0) {
        updated = [...components];
        updated.splice(index + 1, 0, next);
      }
    }
    pushHistoryState(updated, next.id);
    setActiveTextFragmentId(null);
    setActiveSidebar('properties');
  };

  const duplicateComponent = (compId) => {
    const current = components.find((c) => c.id === compId);
    if (!current) return;
    const next = normalizeComponentBindings([cloneComponentTree(current)])[0];
    const idx = components.findIndex((c) => c.id === compId);
    const copy = [...components];
    copy.splice(idx + 1, 0, next);
    pushHistoryState(copy, next.id);
    setActiveTextFragmentId(null);
    setActiveSidebar('properties');
  };

  const removeComponent = (compId) => {
    if (!components.some((component) => component.id === compId)) return;
    pushHistoryState(components.filter((component) => component.id !== compId), selectedId === compId ? null : selectedId);
    if (groupStartId === compId) setGroupStartId(null);
    if (selectedId === compId) setActiveTextFragmentId(null);
    if (activeSmartChipId === compId) setActiveSmartChipId(null);
  };

  const reorderComponents = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const sourceIndex = components.findIndex((item) => item.id === sourceId);
    const targetIndex = components.findIndex((item) => item.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const nextComponents = [...components];
    const [moved] = nextComponents.splice(sourceIndex, 1);
    nextComponents.splice(targetIndex, 0, moved);
    pushHistoryState(nextComponents, selectedId);
  };

  const applyTemplate = (template) => {
    const mapped = normalizeComponentBindings(template.components.map((component) => ({
      ...component,
      id: id(),
      props: { ...component.props }
    })), []);
    replaceComponentsState(mapped, null);
    setActiveTextFragmentId(null);
    setActiveSmartChipId(null);
  };

  const handleImageUpload = async (componentId, file) => {
    if (!file) return;
    if (!user?.uid) {
      toast.error('Please sign in to upload images.');
      return;
    }

    try {
      setUploadingImageId(componentId);
      const safeName = `${Date.now()}_${file.name}`.replace(/\s+/g, '_');
      const imageRef = ref(storage, `forms/${user.uid}/smart-images/${safeName}`);
      const snapshot = await uploadBytes(imageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      const dimensions = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const naturalWidth = img.naturalWidth || 720;
          const naturalHeight = img.naturalHeight || 405;
          const maxWidth = 720;
          const scaledWidth = Math.min(maxWidth, naturalWidth);
          const scaledHeight = Math.max(80, Math.round((naturalHeight / naturalWidth) * scaledWidth));
          resolve({ imgWidth: scaledWidth, imgHeight: scaledHeight });
        };
        img.onerror = () => resolve({});
        img.src = url;
      });

      updateComponent(componentId, {
        src: url,
        alt: file.name || 'Uploaded image',
        ...dimensions
      });
      toast.success('Image uploaded.');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image.');
    } finally {
      setUploadingImageId(null);
    }
  };

  const handleRichMediaUpload = async (componentId, file) => {
    if (!file) return;
    if (!user?.uid) {
      toast.error('Please sign in to upload media.');
      return;
    }

    const mediaType = file.type?.startsWith('video/') ? 'video' : 'image';

    try {
      setUploadingImageId(`${componentId}:rich`);
      const safeName = `${Date.now()}_${file.name}`.replace(/\s+/g, '_');
      const mediaRef = ref(storage, `forms/${user.uid}/smart-rich-media/${safeName}`);
      const snapshot = await uploadBytes(mediaRef, file);
      const url = await getDownloadURL(snapshot.ref);
      const nextItem = {
        id: id(),
        type: mediaType,
        src: url,
        alt: file.name || 'Uploaded media',
        alignment: 'center',
        width: 640,
        height: mediaType === 'video' ? 360 : 240
      };
      const target = components.find((component) => component.id === componentId);
      const media = getRichMediaItems(target?.props || {});
      updateComponent(componentId, { media: [...media, nextItem] });
      toast.success(mediaType === 'video' ? 'Video uploaded.' : 'Image uploaded.');
    } catch (error) {
      console.error('Rich media upload failed:', error);
      toast.error('Failed to upload media.');
    } finally {
      setUploadingImageId(null);
    }
  };

  const runVisionOcrExtraction = async () => {
    if (!file) {
      toast.error('Upload a PDF first to run AI OCR extraction.');
      return;
    }
    if (!user) {
      toast.error('Please sign in to run AI OCR extraction.');
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      toast.error('AI OCR extraction currently supports PDF files.');
      return;
    }

    setIsOcrExtracting(true);
    setOcrProgressLabel('Uploading PDF...');

    try {
      const uploaded = await formService.uploadPDF(file, user.uid);
      setExtractedSourcePdf({
        url: uploaded.url,
        path: uploaded.path,
        name: uploaded.name,
        originalName: uploaded.originalName
      });
      setOcrProgressLabel('Running Vision OCR in Cloud Function...');

      const convertPdf = httpsCallable(functions, 'convertPdfToHtmlForm', { timeout: 540000 });
      const { data: result } = await convertPdf({
        fileUrl: uploaded.url,
        fileName: file.name,
        instructions,
        strategy,
        layoutMode: modeFromOcrMode(ocrMode, layoutMode),
        extractMode: ocrMode
      });

      const smartComponents = Array.isArray(result?.smartDocument?.components)
        ? normalizeComponentBindings(
            result.smartDocument.components.map((c) => ({ ...c, id: c.id || id() })),
            []
          )
        : [];

      if (smartComponents.length === 0) {
        toast.error('No content extracted from AI OCR.');
        return;
      }

      replaceComponentsState(smartComponents, null);
      setActiveSmartChipId(null);
      setDocumentTitle(result?.formTitle || file.name.replace(/\.[^.]+$/, ''));
      setDocConfig((prev) => ({
        ...prev,
        ...(result?.smartDocument?.documentConfig || {}),
        headerText: result?.smartDocument?.documentConfig?.headerText || file.name.replace(/\.[^.]+$/, '') || prev.headerText,
        showHeader: typeof result?.smartDocument?.documentConfig?.showHeader === 'boolean'
          ? result.smartDocument.documentConfig.showHeader
          : true
      }));
      toast.success(`AI OCR extracted ${smartComponents.length} components.`);
    } catch (error) {
      console.error('AI OCR extraction failed:', error);
      toast.error(error?.message || 'AI OCR extraction failed.');
    } finally {
      setIsOcrExtracting(false);
      setOcrProgressLabel('');
    }
  };

  const buildWithAgent = async () => {
    const prompt = `${agentPrompt || ''}`.trim();
    if (!prompt) {
      toast.error('Describe the document or form you want the AI agent to build.');
      return;
    }
    if (!user) {
      toast.error('Please sign in to use the AI builder.');
      return;
    }

    setIsAgentBuilding(true);

    try {
      const buildSmartDocument = httpsCallable(functions, 'buildSmartDocumentFromPrompt', { timeout: 180000 });
      const { data } = await buildSmartDocument({
        prompt,
        intent: agentIntent,
        mode: agentAction,
        strategy,
        theme,
        documentTitle,
        currentComponents: agentAction === 'append' ? components : []
      });

      const rawIncomingComponents = Array.isArray(data?.smartDocument?.components)
        ? data.smartDocument.components.map((component) => ({
            ...component,
            id: id()
          }))
        : [];

      const incomingComponents = agentAction === 'append'
        ? filterAppendOnlyComponents(rawIncomingComponents, components)
        : rawIncomingComponents;

      if (incomingComponents.length === 0) {
        toast.error(agentAction === 'append'
          ? 'The AI agent did not return any new components to append.'
          : 'The AI agent did not return any components.');
        return;
      }

      const hydrated = hydrateGeneratedComponents(incomingComponents);
      const normalized = normalizeComponentBindings(hydrated, agentAction === 'append' ? components : []);

      pushHistoryState(agentAction === 'append' ? [...components, ...normalized] : normalized, normalized[0]?.id || null);
      setActiveTextFragmentId(null);
      setActiveSmartChipId(null);
      setActiveSidebar('properties');
      setInstructions(prompt);

      if (agentAction === 'replace' || !`${documentTitle || ''}`.trim()) {
        setDocumentTitle(data?.formTitle || documentTitle);
      }

      if (data?.smartDocument?.documentConfig) {
        setDocConfig((prev) => ({
          ...prev,
          ...data.smartDocument.documentConfig
        }));
      }

      toast.success(agentAction === 'append' ? 'AI agent added new components.' : 'AI agent built a new draft.');
    } catch (error) {
      console.error('AI builder failed:', error);
      toast.error(error?.message || 'AI builder failed.');
    } finally {
      setIsAgentBuilding(false);
    }
  };

  const persistForm = async (newForm, successMessage = 'Smart document saved successfully.') => {
    const savedForm = await formService.saveForm(newForm, user.uid);
    const isShareable = newForm.visibility === 'public' || newForm.visibility === 'restricted';
    if (isShareable) {
      await formService.publishForm(
        savedForm.id,
        {
          visibility: newForm.visibility,
          allowedEmails: newForm.allowedEmails,
          files: { sourcePdf: newForm.sourcePdf || null },
          smartDocument: newForm.smartDocument || null,
          originalDocumentId: newForm.originalDocumentId || savedForm.id
        },
        user.uid
      );
    }
    setExistingFormMeta((prev) => ({
      id: savedForm.id,
      title: newForm.title || prev?.title || '',
      visibility: newForm.visibility || prev?.visibility || 'private',
      allowedEmails: newForm.allowedEmails || prev?.allowedEmails || [],
      originalDocumentId: newForm.originalDocumentId || prev?.originalDocumentId || savedForm.id,
      shareSettings: newForm.shareSettings || prev?.shareSettings || null,
      sourcePdf: newForm.sourcePdf || prev?.sourcePdf || null
    }));
    try {
      await formService.saveFormBindings(
        {
          formId: savedForm.id,
          title: newForm.title,
          bindings: dataBindings,
          logicMap: flattenComponents(components)
            .filter((component) => component?.props?.visibilityLogic?.rules?.length)
            .map((component) => ({
              componentId: component.id,
              variableName: component?.props?.variableName || '',
              visibilityLogic: component.props.visibilityLogic
            }))
        },
        user.uid
      );
    } catch (bindingError) {
      console.warn('Saving form bindings failed:', bindingError);
    }

    setProgress(100);
    toast.success(successMessage);
    setTimeout(() => {
      navigate(`/form/${savedForm.id}`);
    }, 450);
  };

  const saveDocument = async () => {
    if (!user) {
      toast.error('Please sign in to save a form.');
      return;
    }

    if (components.length === 0) {
      toast.error('Add content or extract with AI OCR before saving.');
      return;
    }

    const flattenedComponents = flattenComponents(components);

    const normalizedVisibility = shareVisibility || 'private';
    const normalizedAllowedEmails = normalizedVisibility === 'restricted'
      ? parseEmailList(shareEmailsInput)
      : [];
    if (normalizedVisibility === 'restricted' && normalizedAllowedEmails.length === 0) {
      toast.error('Add at least one valid email for restricted access.');
      return;
    }

    setIsProcessing(true);
    setProgress(10);

    try {
      const generatedTitle = `${documentTitle || ''}`.trim() ||
        existingFormMeta?.title ||
        `${docConfig.headerText || ''}`.trim() ||
        `Smart Document ${new Date().toLocaleDateString()}`;

      const newForm = {
        title: generatedTitle,
        documentId: existingFormMeta?.id || undefined,
        originalDocumentId: existingFormMeta?.originalDocumentId || existingFormMeta?.id || undefined,
        fields: flattenedComponents.map(normalizeToField).filter(Boolean),
        config: { strategy, theme, brandLogo: null, instructions },
        layoutMode,
        pages: [],
        html: '',
        visibility: normalizedVisibility,
        allowedEmails: normalizedAllowedEmails,
        shareSettings: existingFormMeta?.shareSettings || undefined,
        sourcePdf: existingFormMeta?.sourcePdf || extractedSourcePdf || undefined,
        smartDocument: {
          components,
          documentConfig: docConfig,
          dataBindings
        }
      };

      setProgress(70);
      await persistForm(newForm, 'Smart document saved successfully.');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error?.message || 'Save failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  return (
    <div ref={studioRootRef} className="min-h-screen bg-gradient-to-br from-[#f6f7fb] via-[#eef1ff] to-[#fef7ea]">
      <header className="sticky top-0 z-40 border-b border-[#232753]/20">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/forms')}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-[#4f46e5]"
            >
              <ArrowLeft className="h-4 w-4" />
              Forms
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#232753]">Smart Document Processor</h1>
              <p className="text-xs text-slate-500">
                {editFormId ? 'Editing saved document' : 'Reference UI + logic port with preserved conversion pipeline'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveSidebar('blocks')}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                !isPreviewMode && activeSidebar === 'blocks' ? 'bg-[#232753] text-white' : 'border border-slate-200 bg-white text-slate-700'
              }`}
            >
              Blocks
            </button>
            <button
              type="button"
              onClick={() => setActiveSidebar('properties')}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                !isPreviewMode && activeSidebar === 'properties' ? 'bg-[#232753] text-white' : 'border border-slate-200 bg-white text-slate-700'
              }`}
            >
              Properties
            </button>
            <button
              onClick={() => setIsPreviewMode((prev) => !prev)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                isPreviewMode ? 'bg-[#232753] text-white' : 'border border-slate-200 bg-white text-slate-700'
              }`}
            >
              {isPreviewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {isPreviewMode ? 'Exit Preview' : 'Preview'}
            </button>
            <button
              onClick={() => setShowUploadPanel((prev) => !prev)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                showUploadPanel ? 'bg-[#232753] text-white' : 'border border-slate-200 bg-white text-slate-700'
              }`}
            >
              <Upload className="h-4 w-4" />
              {showUploadPanel ? 'Hide Source' : 'Show Source'}
            </button>
            {!isPreviewMode && (
              <button
                type="button"
                onClick={addPageBreak}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                <Plus className="h-4 w-4" />
                Add Page
              </button>
            )}
            <button
              onClick={saveDocument}
              disabled={isLoadingExisting || isProcessing || components.length === 0}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                isLoadingExisting || isProcessing || components.length === 0 ? 'bg-slate-200 text-slate-500' : 'bg-[#4f46e5] text-white hover:bg-[#4338ca]'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              {isLoadingExisting ? 'Loading...' : isProcessing ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </header>

      <div
        className={`mx-auto grid ${
          isPreviewMode
            ? `${showStudioNavigationPanel ? 'max-w-[1200px]' : 'max-w-[860px]'} grid-cols-1 px-4 pb-8 pt-4 lg:px-6`
            : `max-w-[1680px] grid-cols-1 gap-4 px-4 pb-2 pt-2 lg:h-[calc(100vh-73px)] lg:gap-0 lg:overflow-hidden lg:px-0 lg:pb-2 lg:pt-2 ${
                activeSidebar === 'properties' ? 'lg:grid-cols-[1fr_360px]' : 'lg:grid-cols-[360px_1fr]'
              }`
        }`}
      >
        {!isPreviewMode && activeSidebar === 'blocks' && (
        <aside data-studio-sidebar="true" className="rounded-2xl border border-slate-200 bg-white lg:flex lg:h-[calc(100vh-73px)] lg:flex-col lg:overflow-hidden lg:rounded-none lg:border-y-0 lg:border-l-0 lg:border-r">
          <div className="border-b border-slate-100 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#232753]">Building Blocks</h2>
                <p className="mt-1 text-xs text-slate-500">Extracted from SmartDocs component model</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAgentBuilder((prev) => !prev)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                  showAgentBuilder ? 'bg-[#232753] text-white' : 'border border-slate-200 bg-white text-slate-700'
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {showAgentBuilder ? 'Hide AI Builder' : 'Show AI Builder'}
              </button>
            </div>
          </div>

          <div className="p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            {showAgentBuilder && (
              <div className="rounded-2xl border border-[#4f46e5]/20 bg-gradient-to-br from-[#eef2ff] via-white to-[#f8fafc] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-[#232753]">AI Builder Agent</h3>
                    <p className="mt-1 text-xs text-slate-600">Describe what to build. The agent creates editable blocks and field properties in this studio.</p>
                  </div>
                  <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-[#4f46e5]" />
                </div>

                <textarea
                  rows={5}
                  value={agentPrompt}
                  onChange={(e) => setAgentPrompt(e.target.value)}
                  placeholder="Example: Build a client intake form with contact details, service type, project goals, budget range, timeline, and a consent checkbox."
                  className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
                />

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <select
                    value={agentIntent}
                    onChange={(e) => setAgentIntent(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="auto">Auto Detect Type</option>
                    <option value="form">Build As Form</option>
                    <option value="document">Build As Document</option>
                  </select>
                  <select
                    value={agentAction}
                    onChange={(e) => setAgentAction(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <option value="replace">Replace Current Draft</option>
                    <option value="append">Append To Current Draft</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={buildWithAgent}
                  disabled={isAgentBuilding}
                  className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                    isAgentBuilding ? 'bg-slate-200 text-slate-500' : 'bg-[#232753] text-white hover:bg-[#1b1f47]'
                  }`}
                >
                  <Sparkles className="h-4 w-4" />
                  {isAgentBuilding ? 'Building Draft...' : 'Build With AI Agent'}
                </button>
              </div>
            )}

            <div className={`${showAgentBuilder ? 'mt-4' : 'mt-0'} space-y-2`}>
              {COMPONENT_TYPES.map((item) => (
                <button
                  key={item.type}
                  onClick={() => addComponent(item.type)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:border-[#4f46e5] hover:bg-[#eef2ff]"
                >
                  <span>{item.label}</span>
                  <Plus className="h-4 w-4" />
                </button>
              ))}
            </div>

            <div className="mt-6 border-t border-slate-100 pt-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#232753]">Templates</h3>
              <div className="mt-3 space-y-2">
                {TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-left hover:border-[#4f46e5] hover:bg-[#eef2ff]"
                  >
                    <p className="text-sm font-semibold text-[#232753]">{template.name}</p>
                    <p className="text-xs text-slate-500">{template.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>
        )}

        <main
          className={`${
            isPreviewMode
              ? 'min-h-[calc(100vh-96px)] bg-transparent py-2'
              : 'bg-transparent lg:h-[calc(100vh-73px)] lg:overflow-hidden'
          }`}
        >
          <div className="mx-auto w-full max-w-none space-y-4 lg:h-full lg:overflow-y-auto">
            {showUploadPanel && !isPreviewMode && (
            <section className="rounded-2xl border border-[#232753]/20 bg-white p-4 shadow-sm">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="block rounded-xl border border-slate-200 p-3">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Upload PDF</span>
                  <div className="flex items-center gap-2">
                    <Upload className="h-4 w-4 text-[#4f46e5]" />
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-[#232753] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">{file ? file.name : 'No file selected (PDF or image)'}</p>
                </label>

                <label className="block rounded-xl border border-slate-200 p-3">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">AI Instructions</span>
                  <textarea
                    rows={4}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Example: Keep legal wording but modernize spacing and labels."
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30"
                  />
                </label>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-4">
                <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="strict">Strict Strategy</option>
                  <option value="creative">Creative Strategy</option>
                </select>
                <select value={layoutMode} onChange={(e) => setLayoutMode(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="original">Original Layout</option>
                  <option value="reflow">Reflow Layout</option>
                </select>
                <select value={theme} onChange={(e) => setTheme(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="light">Light Theme</option>
                  <option value="dark">Dark Theme</option>
                </select>
                <select value={ocrMode} onChange={(e) => setOcrMode(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                  <option value="both">OCR: Text + Fields</option>
                  <option value="text">OCR: Text Only</option>
                  <option value="fields">OCR: Fields Only</option>
                </select>
              </div>

              <div className="mt-3 rounded-xl border border-[#232753]/20 bg-[#232753]/5 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between text-xs font-semibold text-[#232753]">
                      <span className="truncate">{ocrProgressLabel || 'AI OCR extraction'}</span>
                      <span>{isOcrExtracting ? 'Running' : 'Ready'}</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#232753]/15">
                      <div className={`h-full ${isOcrExtracting ? 'animate-pulse bg-[#232753]' : 'bg-[#232753]/40'}`} style={{ width: isOcrExtracting ? '100%' : '0%' }} />
                    </div>
                  </div>
                  <button
                    onClick={runVisionOcrExtraction}
                    disabled={isOcrExtracting || !file}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
                      isOcrExtracting || !file ? 'bg-slate-200 text-slate-500' : 'bg-[#232753] text-white hover:bg-[#1b1f47]'
                    }`}
                  >
                    <Wand2 className="h-4 w-4 text-[#4f46e5]" />
                    {isOcrExtracting ? 'Extracting...' : 'Extract with AI OCR'}
                  </button>
                </div>
              </div>

              {isProcessing && (
                <div className="mt-4 rounded-xl border border-[#4f46e5]/40 bg-[#eef2ff] p-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-[#232753]">
                    <span>Processing pipeline</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#232753]/15">
                    <div className="h-full bg-[#4f46e5] transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}
            </section>
            )}

            <section className="block">
              {showStudioNavigationPanel && (
                <SmartNavigationPanel
                  items={navigationItems}
                  title={docConfig.navigationMenuTitle}
                  fixedClass="lg:left-6 lg:top-24 lg:bottom-6"
                />
              )}
              <div className={`min-w-0 ${isPreviewMode ? 'overflow-x-auto px-0 pb-0 lg:overflow-x-visible' : 'overflow-x-auto pb-2'} ${showStudioNavigationPanel ? 'lg:ml-80' : ''}`}>
                <div className="space-y-6">
                  {paginatedPages.map((pageItems, pageIndex) => (
                    <div
                      key={`studio-page-${pageIndex}`}
                      className={`mx-auto border border-slate-200 bg-white ${isPreviewMode ? 'shadow-xl' : 'shadow-sm'}`}
                      style={{ width: `${A4_WIDTH_PX}px`, minWidth: `${A4_WIDTH_PX}px`, height: `${A4_HEIGHT_PX}px` }}
                    >
                      <div className="flex h-full flex-col">
                        {docConfig.showHeader && (
                          <div className="box-border border-b border-slate-100 px-12 pb-2 pt-6 text-xs text-slate-400" style={{ height: `${HEADER_SPACE_PX}px` }}>
                            {docConfig.headerText}
                          </div>
                        )}

                        <div
                          className="box-border shrink-0 overflow-hidden px-12"
                          style={{
                            height: `${pageBodyHeight}px`,
                            paddingTop: `${PAGE_CONTENT_PADDING_TOP_PX}px`,
                            paddingBottom: `${PAGE_CONTENT_PADDING_BOTTOM_PX + TEXT_LAYOUT_SAFETY_PX}px`
                          }}
                        >
                          {components.length === 0 && pageIndex === 0 ? (
                            <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
                              <Wand2 className="mx-auto h-8 w-8 text-[#4f46e5]" />
                              <p className="mt-2 text-sm text-slate-500">Add components from the left to build your smart document.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-12 gap-2">
                              {pageItems.map((item) => {
                                const component = item.component;
                                const renderComponent = item.renderComponent;
                                const isNavigationMenu = Boolean(component?.props?.isGeneratedNavigationMenu);
                                const sectionAnchorId = ['heading', 'sectionDivider'].includes(component?.type)
                                  ? `smart-section-${component.id}`
                                  : undefined;
                                const canDrag = !isPreviewMode && !isNavigationMenu && !item.isFragment && !['pageBreak', 'textBlock', 'richContent'].includes(component.type);
                                const fragmentToken = `${component.id}:${Number.isFinite(renderComponent?.props?.fragmentStart) ? renderComponent.props.fragmentStart : 0}`;
                                const pendingCaretRequest = pendingCaretRequestRef.current;
                                const fragmentStart = Number.isFinite(renderComponent?.props?.fragmentStart) ? renderComponent.props.fragmentStart : 0;
                                const fragmentEnd = Number.isFinite(renderComponent?.props?.fragmentEnd)
                                  ? renderComponent.props.fragmentEnd
                                  : fragmentStart + getTextBlockPlainText(renderComponent?.props || {}).length;
                                const pendingCaretOffset = pendingCaretRequest?.componentId === component.id &&
                                  Number.isFinite(pendingCaretRequest?.absoluteOffset) &&
                                  pendingCaretRequest.absoluteOffset >= fragmentStart &&
                                  pendingCaretRequest.absoluteOffset <= fragmentEnd
                                  ? pendingCaretRequest.absoluteOffset - fragmentStart
                                  : null;
                                const isActiveTextFragment = component.type === 'textBlock'
                                  ? activeTextFragmentId === fragmentToken || Number.isFinite(pendingCaretOffset)
                                  : false;

                                return (
                                  <div
                                    key={item.key}
                                    id={sectionAnchorId}
                                    data-studio-component-id={component.id}
                                    draggable={canDrag}
                                    onDragStart={(e) => {
                                      if (!canDrag) return;
                                      setDraggingId(component.id);
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', component.id);
                                    }}
                                    onDragOver={(e) => {
                                      if (isPreviewMode || !draggingId || draggingId === component.id || item.isFragment) return;
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                      setDragOverId(component.id);
                                    }}
                                    onDragLeave={() => {
                                      if (dragOverId === component.id) setDragOverId(null);
                                    }}
                                    onDrop={(e) => {
                                      if (isPreviewMode || item.isFragment) return;
                                      e.preventDefault();
                                      const sourceId = e.dataTransfer.getData('text/plain') || draggingId;
                                      reorderComponents(sourceId, component.id);
                                      setDraggingId(null);
                                      setDragOverId(null);
                                    }}
                                    onDragEnd={() => {
                                      setDraggingId(null);
                                      setDragOverId(null);
                                    }}
                                    onClick={() => {
                                      if (!isPreviewMode && !isNavigationMenu) {
                                        setSelectedId(component.id);
                                        setActiveTextFragmentId(component.type === 'textBlock' ? fragmentToken : null);
                                        setActiveSidebar('properties');
                                        if (activeSmartChipId && activeSmartChipId !== component.id) {
                                          setActiveSmartChipId(null);
                                        }
                                      }
                                    }}
                                    className={`relative rounded-xl transition ${getWidthClass(component?.props?.width)} ${
                                      isPreviewMode || isNavigationMenu ? '' : 'cursor-pointer'
                                    }`}
                                  >
                                    {!isPreviewMode && groupStartId === component.id && selectedId !== component.id && (
                                      <div className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-amber-400 ring-offset-2 ring-offset-white" />
                                    )}

                                    {!isPreviewMode && !isNavigationMenu && selectedId === component.id && (
                                      <div className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-[#4f46e5]" />
                                    )}

                                    {!isPreviewMode && dragOverId === component.id && (
                                      <div className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-emerald-400" />
                                    )}

                                    <RenderComponent
                                      component={renderComponent}
                                      onUpdate={component.type === 'richContent' && item.isFragment
                                        ? (compId, updates) => updateRichContentFragment(compId, renderComponent.props, updates)
                                        : updateComponent}
                                      onUploadRichMedia={handleRichMediaUpload}
                                      onBoundaryAction={handleTextBoundaryAction}
                                      isSelected={component.type === 'textBlock' ? isActiveTextFragment : selectedId === component.id}
                                      isPreviewMode={isPreviewMode}
                                      allowInternalOverflow={Boolean(item.allowInternalOverflow)}
                                      maxContentHeight={item.allowInternalOverflow ? pageContentHeight - 64 : null}
                                      pendingCaretOffset={pendingCaretOffset}
                                    />

                                    {component.props?.smartChipConfig?.enabled && component.type !== 'pageBreak' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveSmartChipId((prev) => (prev === component.id ? null : component.id));
                                        }}
                                        className={`absolute z-20 rounded-full border px-2 py-1 text-[10px] font-bold shadow-sm ${
                                          component.props?.smartChipData?.text ||
                                          Number(component.props?.smartChipData?.rating || 0) > 0 ||
                                          Number(component.props?.smartChipData?.progress || 0) > 0 ||
                                          ['red', 'orange', 'green'].includes(component.props?.smartChipData?.status)
                                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                            : 'border-slate-200 bg-white text-slate-400 hover:border-[#4f46e5] hover:text-[#232753]'
                                        } ${
                                          component.props.smartChipConfig?.chipPosition === 'top-left'
                                            ? '-left-2 -top-2'
                                            : component.props.smartChipConfig?.chipPosition === 'top-right'
                                              ? '-right-2 -top-2'
                                              : component.props.smartChipConfig?.chipPosition === 'bottom-left'
                                                ? '-bottom-2 -left-2'
                                                : '-bottom-2 -right-2'
                                        }`}
                                      >
                                        {component.props.smartChipConfig?.chipStyle === 'icon-only'
                                          ? 'SC'
                                          : component.props.smartChipConfig?.chipStyle === 'minimal'
                                            ? 'Smart'
                                            : 'Smart Chip'}
                                      </button>
                                    )}

                                    {activeSmartChipId === component.id && component.props?.smartChipConfig?.enabled && (
                                      <SmartChipModal
                                        config={component.props.smartChipConfig}
                                        initialData={component.props.smartChipData}
                                        onSave={(data) => {
                                          updateComponent(component.id, { smartChipData: data });
                                          setActiveSmartChipId(null);
                                        }}
                                        onClose={() => setActiveSmartChipId(null)}
                                      />
                                    )}

                                    {!isPreviewMode && !isNavigationMenu && !item.isFragment && selectedId === component.id && (
                                      <div className="absolute right-2 top-2 z-20 flex items-center gap-2 rounded-lg border border-slate-200 bg-white/95 p-1 shadow-sm backdrop-blur">
                                        {!isGroupComponent(component) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (groupStartId && groupStartId !== component.id) {
                                                groupComponentRange(groupStartId, component.id);
                                                return;
                                              }
                                              setGroupStartId((prev) => (prev === component.id ? null : component.id));
                                            }}
                                            className={`rounded-lg border p-1.5 text-slate-600 ${
                                              groupStartId === component.id
                                                ? 'border-amber-300 bg-amber-50 text-amber-700'
                                                : 'border-slate-200 hover:border-[#4f46e5]'
                                            }`}
                                            title={groupStartId && groupStartId !== component.id ? 'Group selected range' : (groupStartId === component.id ? 'Cancel group start' : 'Start group')}
                                          >
                                            <CheckCircle2 className="h-4 w-4" />
                                          </button>
                                        )}
                                        {isGroupComponent(component) && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); ungroupComponent(component.id); }}
                                            className="rounded-lg border border-amber-200 p-1.5 text-amber-700 hover:bg-amber-50"
                                            title="Ungroup"
                                          >
                                            <Wand2 className="h-4 w-4" />
                                          </button>
                                        )}
                                        <button
                                          onClick={(e) => { e.stopPropagation(); moveComponentByOffset(component.id, -1); }}
                                          disabled={selectedIndex <= 0}
                                          className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:border-[#4f46e5] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                          <ArrowUp className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); moveComponentByOffset(component.id, 1); }}
                                          disabled={selectedIndex < 0 || selectedIndex >= components.length - 1}
                                          className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:border-[#4f46e5] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                          <ArrowDown className="h-4 w-4" />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); duplicateComponent(component.id); }} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:border-[#4f46e5]">
                                          <Copy className="h-4 w-4" />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); removeComponent(component.id); }} className="rounded-lg border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50">
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {docConfig.showFooter && (
                          <div className="box-border border-t border-slate-100 px-12 pb-6 pt-2 text-xs text-slate-400" style={{ height: `${FOOTER_SPACE_PX}px` }}>
                            {docConfig.footerText}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </main>
        {!isPreviewMode && activeSidebar === 'properties' && (
        <aside data-studio-sidebar="true" className="rounded-2xl border border-slate-200 bg-white lg:flex lg:h-[calc(100vh-73px)] lg:flex-col lg:overflow-hidden lg:rounded-none lg:border-y-0 lg:border-l lg:border-r-0">
          <div className="border-b border-slate-100 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#232753]">Properties</h2>
                <p className="mt-1 text-xs text-slate-500">Component logic and document settings</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={undoLastChange}
                  disabled={componentHistory.length === 0}
                  className={`rounded-lg p-2 ${
                    componentHistory.length === 0 ? 'bg-slate-100 text-slate-400' : 'border border-slate-200 bg-white text-slate-700 hover:border-[#4f46e5]'
                  }`}
                  title="Undo"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={redoLastChange}
                  disabled={redoHistory.length === 0}
                  className={`rounded-lg p-2 ${
                    redoHistory.length === 0 ? 'bg-slate-100 text-slate-400' : 'border border-slate-200 bg-white text-slate-700 hover:border-[#4f46e5]'
                  }`}
                  title="Redo"
                >
                  <RotateCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-[calc(100vh-120px)] space-y-5 overflow-y-auto p-4 lg:min-h-0 lg:max-h-none lg:flex-1">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Document Title</label>
                <input
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="Untitled document"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>

              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sharing Access</p>
                <p className="mt-1 text-xs text-slate-500">Choose who can open this form link after save.</p>

                <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-slate-500">Visibility</label>
                <select
                  value={shareVisibility}
                  onChange={(e) => setShareVisibility(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="private">Private (owner only)</option>
                  <option value="public">Public (anyone with link)</option>
                  <option value="restricted">Restricted (specific emails)</option>
                </select>

                {shareVisibility === 'restricted' && (
                  <div className="mt-3">
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Allowed Emails (comma-separated)
                    </label>
                    <textarea
                      rows={3}
                      value={shareEmailsInput}
                      onChange={(e) => setShareEmailsInput(e.target.value)}
                      placeholder="name@company.com, teammate@company.com"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    <p className="mt-2 text-xs text-slate-500">Only listed emails can access when visibility is restricted.</p>
                  </div>
                )}
              </div>
            </div>

            {!selected && (
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Header Text</label>
                  <input
                    value={docConfig.headerText}
                    onChange={(e) => setDocConfig((prev) => ({ ...prev, headerText: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Footer Text</label>
                  <input
                    value={docConfig.footerText}
                    onChange={(e) => setDocConfig((prev) => ({ ...prev, footerText: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={docConfig.showHeader}
                    onChange={(e) => setDocConfig((prev) => ({ ...prev, showHeader: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  Show Header
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={docConfig.showFooter}
                    onChange={(e) => setDocConfig((prev) => ({ ...prev, showFooter: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  Show Footer
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(docConfig.showNavigationMenu)}
                    onChange={(e) => setDocConfig((prev) => ({ ...prev, showNavigationMenu: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  Show navigation panel
                </label>
                {docConfig.showNavigationMenu && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Navigation Panel Title</label>
                    <input
                      value={docConfig.navigationMenuTitle || ''}
                      onChange={(e) => setDocConfig((prev) => ({ ...prev, navigationMenuTitle: e.target.value }))}
                      placeholder="Document Navigation"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      The left panel is generated from headings and section dividers in this document.
                    </p>
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data Bindings</p>
                  {dataBindings.length === 0 ? (
                    <p className="mt-2 text-xs text-slate-500">Select a component to configure variable names for logic and persistence.</p>
                  ) : (
                    <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                      {dataBindings.map((binding) => (
                        <div key={binding.componentId} className="rounded border border-slate-200 px-2 py-1">
                          <p className="font-mono text-xs text-[#232753]">{binding.variableName}</p>
                          <p className="text-[11px] text-slate-500">{binding.componentType}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selected && (
              <div className="space-y-4">
                <div className="rounded-xl border border-[#4f46e5]/40 bg-[#eef2ff] p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#232753]">Selected Component</p>
                  <p className="mt-1 text-sm font-semibold text-[#232753]">
                    {isGroupComponent(selected) ? 'grouped block' : selected.type}
                  </p>
                </div>

                {!isGroupComponent(selected) && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Component Type</label>
                    <select
                      value={selected.type}
                      onChange={(e) => changeComponentType(selected.id, e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      {COMPONENT_TYPES.map((item) => (
                        <option key={`component-type-${item.type}`} value={item.type}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-slate-500">Switch field type while preserving shared properties and binding where possible.</p>
                  </div>
                )}

                {isGroupComponent(selected) && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Group Controls</p>
                    <p className="mt-2 text-sm text-slate-600">
                      This grouped block contains {flattenComponents(selected.children || []).length} component(s). Ungroup it to edit individual blocks.
                    </p>
                    <button
                      type="button"
                      onClick={() => ungroupComponent(selected.id)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-white px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50"
                    >
                      <Wand2 className="h-4 w-4" />
                      Ungroup Block
                    </button>
                  </div>
                )}

                {!isGroupComponent(selected) && (selected.type === 'heading' || selected.type === 'sectionDivider') && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {selected.type === 'sectionDivider' ? 'Divider Label' : 'Content'}
                    </label>
                    <textarea
                      rows={selected.type === 'sectionDivider' ? 2 : 4}
                      value={selected.props.content || ''}
                      onChange={(e) => updateComponent(selected.id, { content: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                )}

                {!isGroupComponent(selected) && selected.type === 'textBlock' && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Text Block Editing</p>
                    <p className="mt-2 text-sm text-slate-600">Edit this block directly on the page.</p>
                    <p className="mt-2 text-xs text-slate-500">If the block is longer than one page, it stays on the current page with vertical scroll so you can manually cut overflow text and move it to the next page.</p>
                  </div>
                )}

                {!isGroupComponent(selected) && selected.type === 'richContent' && (
                  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rich Content</p>
                      <p className="mt-2 text-xs text-slate-500">Select the block to open its complete content in a stable scrollable editor. When you deselect it or preview the document, long content and media paginate automatically.</p>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => {
                          const media = getRichMediaItems(selected.props);
                          updateComponent(selected.id, {
                            media: [
                              ...media,
                              { id: id(), type: 'image', src: 'https://picsum.photos/seed/rich-content/960/540', alt: 'Rich content image', alignment: 'center', width: 640, height: 360 }
                            ]
                          });
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#4f46e5]"
                      >
                        Add Image URL
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const media = getRichMediaItems(selected.props);
                          updateComponent(selected.id, {
                            media: [
                              ...media,
                              { id: id(), type: 'video', src: '', alt: 'Rich content video', alignment: 'center', width: 640, height: 360 }
                            ]
                          });
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#4f46e5]"
                      >
                        Add Video URL
                      </button>
                    </div>
                    <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-[#4f46e5] hover:text-[#232753]">
                      <Upload className="h-4 w-4" />
                      <span>{uploadingImageId === `${selected.id}:rich` ? 'Uploading...' : 'Upload Image or Video'}</span>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        disabled={uploadingImageId === `${selected.id}:rich`}
                        onChange={(e) => handleRichMediaUpload(selected.id, e.target.files?.[0])}
                        className="hidden"
                      />
                    </label>
                    {getRichMediaItems(selected.props).map((item, mediaIndex) => (
                      <div key={item.id || `${selected.id}-rich-media-${mediaIndex}`} className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {item.type === 'video' ? 'Video' : 'Image'} {mediaIndex + 1}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              const media = getRichMediaItems(selected.props).filter((_, index) => index !== mediaIndex);
                              updateComponent(selected.id, { media });
                            }}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          value={item.src || ''}
                          onChange={(e) => {
                            const media = getRichMediaItems(selected.props).map((mediaItem, index) => (
                              index === mediaIndex ? { ...mediaItem, src: e.target.value } : mediaItem
                            ));
                            updateComponent(selected.id, { media });
                          }}
                          placeholder={item.type === 'video' ? 'https://...mp4' : 'https://...image.jpg'}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                        <div className="grid gap-2 sm:grid-cols-3">
                          <select
                            value={item.alignment || 'center'}
                            onChange={(e) => {
                              const media = getRichMediaItems(selected.props).map((mediaItem, index) => (
                                index === mediaIndex ? { ...mediaItem, alignment: e.target.value } : mediaItem
                              ));
                              updateComponent(selected.id, { media });
                            }}
                            className="rounded-lg border border-slate-200 px-2 py-2 text-sm"
                          >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                          <input
                            type="number"
                            min="120"
                            max="760"
                            value={Number(item.width) || 640}
                            onChange={(e) => {
                              const media = getRichMediaItems(selected.props).map((mediaItem, index) => (
                                index === mediaIndex ? { ...mediaItem, width: Number(e.target.value) } : mediaItem
                              ));
                              updateComponent(selected.id, { media });
                            }}
                            className="rounded-lg border border-slate-200 px-2 py-2 text-sm"
                          />
                          <input
                            type="number"
                            min="90"
                            max="720"
                            value={Number(item.height) || getRichMediaHeight(item)}
                            onChange={(e) => {
                              const media = getRichMediaItems(selected.props).map((mediaItem, index) => (
                                index === mediaIndex ? { ...mediaItem, height: Number(e.target.value) } : mediaItem
                              ));
                              updateComponent(selected.id, { media });
                            }}
                            className="rounded-lg border border-slate-200 px-2 py-2 text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!isGroupComponent(selected) && selected.type !== 'image' && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Alignment</label>
                    <select
                      value={selected.props.alignment || 'left'}
                      onChange={(e) => updateComponent(selected.id, { alignment: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                )}

                {!isGroupComponent(selected) && selected.type !== 'image' && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Text Size</label>
                    <select
                      value={selected.props.textSize || (selected.type === 'heading' ? '2xl' : 'sm')}
                      onChange={(e) => updateComponent(selected.id, { textSize: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="xs">Extra Small</option>
                      <option value="sm">Small</option>
                      <option value="base">Base</option>
                      <option value="lg">Large</option>
                      <option value="xl">Extra Large</option>
                      <option value="2xl">2XL</option>
                      <option value="3xl">3XL</option>
                    </select>
                  </div>
                )}

                {!isGroupComponent(selected) && (
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Row Width</label>
                  <select
                    value={selected.props.width || 'full'}
                    onChange={(e) => updateComponent(selected.id, { width: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="full">Full Width</option>
                    <option value="two-thirds">Two Thirds</option>
                    <option value="half">Half Width</option>
                    <option value="third">One Third</option>
                  </select>
                  <p className="mt-2 text-xs text-slate-500">Use Half/Third to place components side by side in the same row.</p>
                </div>
                )}

                {!isGroupComponent(selected) && selected.type === 'sectionDivider' && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={selected.props.showLabel !== false}
                        onChange={(e) => updateComponent(selected.id, { showLabel: e.target.checked })}
                        className="rounded border-slate-300"
                      />
                      Show divider label
                    </label>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Line Style</label>
                      <select
                        value={selected.props.lineStyle || 'solid'}
                        onChange={(e) => updateComponent(selected.id, { lineStyle: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Line Color</label>
                      <input
                        value={selected.props.lineColor || '#cbd5e1'}
                        onChange={(e) => updateComponent(selected.id, { lineColor: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Line Thickness</label>
                      <input
                        type="number"
                        min="1"
                        max="6"
                        value={selected.props.lineThickness ?? 1}
                        onChange={(e) => updateComponent(selected.id, { lineThickness: Number(e.target.value) })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Top Spacing</label>
                        <input
                          type="number"
                          min="0"
                          max="80"
                          value={selected.props.spacingTop ?? 24}
                          onChange={(e) => updateComponent(selected.id, { spacingTop: Number(e.target.value) })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Bottom Spacing</label>
                        <input
                          type="number"
                          min="0"
                          max="80"
                          value={selected.props.spacingBottom ?? 24}
                          onChange={(e) => updateComponent(selected.id, { spacingBottom: Number(e.target.value) })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </>
                )}

                {!isGroupComponent(selected) && selected.type === 'image' && (
                  <>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Image URL</label>
                      <input
                        value={selected.props.src || ''}
                        onChange={(e) => updateComponent(selected.id, { src: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Upload From Device</label>
                      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:border-[#4f46e5] hover:text-[#232753]">
                        <Upload className="h-4 w-4" />
                        <span>{uploadingImageId === selected.id ? 'Uploading...' : 'Choose Image'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          disabled={uploadingImageId === selected.id}
                          onChange={(e) => handleImageUpload(selected.id, e.target.files?.[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Alt Text</label>
                      <input
                        value={selected.props.alt || ''}
                        onChange={(e) => updateComponent(selected.id, { alt: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Crop Zoom</label>
                        <span className="text-xs font-medium text-slate-600">{Number(selected.props.cropScale || 1).toFixed(2)}x</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.05"
                        value={selected.props.cropScale || 1}
                        onChange={(e) => updateComponent(selected.id, { cropScale: Number(e.target.value) })}
                        className="w-full"
                      />
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Focus X</label>
                        <span className="text-xs font-medium text-slate-600">{Math.round(Number(selected.props.cropX ?? 50))}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={selected.props.cropX ?? 50}
                        onChange={(e) => updateComponent(selected.id, { cropX: Number(e.target.value) })}
                        className="w-full"
                      />
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Focus Y</label>
                        <span className="text-xs font-medium text-slate-600">{Math.round(Number(selected.props.cropY ?? 50))}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={selected.props.cropY ?? 50}
                        onChange={(e) => updateComponent(selected.id, { cropY: Number(e.target.value) })}
                        className="w-full"
                      />
                      <p className="mt-3 text-xs text-slate-500">Crop applies inside the current image frame. Resize the image first if you need a tighter crop window.</p>
                    </div>
                  </>
                )}

                {!isGroupComponent(selected) && (
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data Binding</p>
                  {isBindableComponent(selected.type) ? (
                    <>
                      <label className="mb-1 mt-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Variable Name</label>
                      <input
                        value={selected.props.variableName || ''}
                        onChange={(e) => {
                          const nextName = ensureUniqueBinding(e.target.value, selected.id);
                          updateComponent(selected.id, { variableName: nextName });
                        }}
                        placeholder="e.g., first_name"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm"
                      />
                      <p className="mt-2 text-xs text-slate-500">Used as the reference key in logic rules, form payloads, and saved bindings.</p>
                    </>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500">This component type does not create a data-binding key.</p>
                  )}
                </div>
                )}

                {!isGroupComponent(selected) && ['textInput', 'textareaInput', 'dropdown', 'radio', 'checklist', 'checkbox', 'datePicker', 'barcodeInput', 'qrCodeInput', 'nfcInput', 'rating', 'progress', 'trafficLightStatus'].includes(selected.type) && (
                  <>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Label</label>
                      <input
                        value={selected.props.label || ''}
                        onChange={(e) => updateComponent(selected.id, { label: e.target.value })}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      />
                    </div>

                    {['textInput', 'textareaInput', 'barcodeInput', 'qrCodeInput', 'nfcInput'].includes(selected.type) && (
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Placeholder</label>
                        <input
                          value={selected.props.placeholder || ''}
                          onChange={(e) => updateComponent(selected.id, { placeholder: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    )}

                    {selected.type === 'textareaInput' && (
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Rows</label>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={selected.props.rows ?? 3}
                          onChange={(e) => updateComponent(selected.id, { rows: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    )}

                    {['dropdown', 'radio', 'checklist'].includes(selected.type) && (
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Options</label>
                        <div className="space-y-2">
                          {(Array.isArray(selected.props.options) ? selected.props.options : []).map((option, optionIndex) => (
                            <div key={`${selected.id}-option-${optionIndex}`} className="flex items-center gap-2">
                              <input
                                value={option}
                                onChange={(e) => {
                                  const nextOptions = [...(Array.isArray(selected.props.options) ? selected.props.options : [])];
                                  nextOptions[optionIndex] = e.target.value;
                                  updateComponent(selected.id, { options: nextOptions });
                                }}
                                placeholder={`Option ${optionIndex + 1}`}
                                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const currentOptions = Array.isArray(selected.props.options) ? selected.props.options : [];
                                  if (currentOptions.length <= 1) return;
                                  const nextOptions = currentOptions.filter((_, idx) => idx !== optionIndex);
                                  updateComponent(selected.id, { options: nextOptions });
                                }}
                                disabled={(Array.isArray(selected.props.options) ? selected.props.options.length : 0) <= 1}
                                className="rounded-lg border border-rose-200 px-2 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const currentOptions = Array.isArray(selected.props.options) ? selected.props.options : [];
                              updateComponent(selected.id, { options: [...currentOptions, `Option ${currentOptions.length + 1}`] });
                            }}
                            className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#4f46e5] hover:text-[#232753]"
                          >
                            Add Option
                          </button>
                        </div>
                      </div>
                    )}

                    {selected.type === 'progress' && (
                      <>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Progress Value</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={selected.props.progressValue ?? 0}
                            onChange={(e) => updateComponent(selected.id, { progressValue: Number(e.target.value) })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Progress Color</label>
                          <input
                            value={selected.props.progressColor || '#4f46e5'}
                            onChange={(e) => updateComponent(selected.id, { progressColor: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          />
                        </div>
                      </>
                    )}

                    {selected.type === 'rating' && (
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Max Rating</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={selected.props.maxRating ?? 5}
                          onChange={(e) => updateComponent(selected.id, { maxRating: Number(e.target.value) })}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                    )}

                    {selected.type === 'trafficLightStatus' && (
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Current Status</label>
                        <select
                          value={selected.props.value || 'orange'}
                          onChange={(e) => updateComponent(selected.id, { value: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 px-2 py-1 text-sm"
                        >
                          <option value="red">Red</option>
                          <option value="orange">Orange</option>
                          <option value="green">Green</option>
                        </select>
                      </div>
                    )}

                    {['textInput', 'textareaInput', 'dropdown', 'radio', 'checklist', 'checkbox', 'datePicker', 'barcodeInput', 'qrCodeInput', 'nfcInput'].includes(selected.type) && (
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(selected.props.required)}
                          onChange={(e) => updateComponent(selected.id, { required: e.target.checked })}
                          className="rounded border-slate-300"
                        />
                        Required field
                      </label>
                    )}
                  </>
                )}

                {!isGroupComponent(selected) && (
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Smart Chip</p>
                  <p className="mt-1 text-xs text-slate-500">Attach interactive notes, rating, progress, or status to this block.</p>

                  <div className="mt-3 space-y-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(selected.props.smartChipConfig?.enabled)}
                        onChange={(e) => {
                          const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                          updateComponent(selected.id, { smartChipConfig: { ...prev, enabled: e.target.checked } });
                        }}
                        className="rounded border-slate-300"
                      />
                      Enable Smart Chip
                    </label>

                    {selected.props.smartChipConfig?.enabled && (
                      <>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={selected.props.smartChipConfig?.enableText !== false}
                            onChange={(e) => {
                              const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                              updateComponent(selected.id, { smartChipConfig: { ...prev, enableText: e.target.checked } });
                            }}
                            className="rounded border-slate-300"
                          />
                          Chip Notes
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(selected.props.smartChipConfig?.enableRating)}
                            onChange={(e) => {
                              const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                              updateComponent(selected.id, { smartChipConfig: { ...prev, enableRating: e.target.checked } });
                            }}
                            className="rounded border-slate-300"
                          />
                          Chip Rating
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(selected.props.smartChipConfig?.enableProgress)}
                            onChange={(e) => {
                              const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                              updateComponent(selected.id, { smartChipConfig: { ...prev, enableProgress: e.target.checked } });
                            }}
                            className="rounded border-slate-300"
                          />
                          Chip Progress
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(selected.props.smartChipConfig?.enableStatus)}
                            onChange={(e) => {
                              const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                              updateComponent(selected.id, { smartChipConfig: { ...prev, enableStatus: e.target.checked } });
                            }}
                            className="rounded border-slate-300"
                          />
                          Chip Status (ROG)
                        </label>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Chip Position</label>
                          <select
                            value={selected.props.smartChipConfig?.chipPosition || 'bottom-right'}
                            onChange={(e) => {
                              const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                              updateComponent(selected.id, { smartChipConfig: { ...prev, chipPosition: e.target.value } });
                            }}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          >
                            <option value="bottom-right">Bottom Right</option>
                            <option value="top-right">Top Right</option>
                            <option value="bottom-left">Bottom Left</option>
                            <option value="top-left">Top Left</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Chip Style</label>
                          <select
                            value={selected.props.smartChipConfig?.chipStyle || 'default'}
                            onChange={(e) => {
                              const prev = selected.props.smartChipConfig || DEFAULT_SMART_CHIP_CONFIG;
                              updateComponent(selected.id, { smartChipConfig: { ...prev, chipStyle: e.target.value } });
                            }}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          >
                            <option value="default">Default</option>
                            <option value="minimal">Minimal</option>
                            <option value="icon-only">Icon Only</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                )}

                {!isGroupComponent(selected) && (
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conditional Visibility</p>
                  <p className="mt-1 text-xs text-slate-500">Show/hide based on another field value.</p>

                  <div className="mt-3 space-y-2">
                    <select
                      value={selected.props.visibilityLogic?.action || 'show'}
                      onChange={(e) => {
                        const prev = selected.props.visibilityLogic || { action: 'show', condition: 'all', rules: [] };
                        updateComponent(selected.id, { visibilityLogic: { ...prev, action: e.target.value } });
                      }}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="show">Show when rule matches</option>
                      <option value="hide">Hide when rule matches</option>
                    </select>

                    <select
                      value={selected.props.visibilityLogic?.condition || 'all'}
                      onChange={(e) => {
                        const prev = selected.props.visibilityLogic || { action: 'show', condition: 'all', rules: [] };
                        updateComponent(selected.id, { visibilityLogic: { ...prev, condition: e.target.value } });
                      }}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="all">All rules</option>
                      <option value="any">Any rule</option>
                    </select>

                    {(selected.props.visibilityLogic?.rules || []).map((rule, idx) => (
                      <div key={`${selected.id}-rule-${idx}`} className="space-y-2 rounded-lg border border-slate-200 p-2">
                        <select
                          value={rule.fieldId || ''}
                          onChange={(e) => {
                            const rules = [...(selected.props.visibilityLogic?.rules || [])];
                            rules[idx] = { ...rules[idx], fieldId: e.target.value };
                            updateComponent(selected.id, {
                              visibilityLogic: { ...(selected.props.visibilityLogic || { action: 'show', condition: 'all' }), rules }
                            });
                          }}
                          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
                        >
                          <option value="">Select field variable</option>
                          {dataBindings
                            .filter((binding) => binding.componentId !== selected.id)
                            .map((binding) => (
                              <option key={`${selected.id}-binding-${idx}-${binding.componentId}`} value={binding.variableName}>
                                {binding.variableName} ({binding.componentType})
                              </option>
                            ))}
                        </select>
                        <select
                          value={rule.operator || 'equals'}
                          onChange={(e) => {
                            const rules = [...(selected.props.visibilityLogic?.rules || [])];
                            rules[idx] = { ...rules[idx], operator: e.target.value };
                            updateComponent(selected.id, {
                              visibilityLogic: { ...(selected.props.visibilityLogic || { action: 'show', condition: 'all' }), rules }
                            });
                          }}
                          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
                        >
                          <option value="equals">Equals</option>
                          <option value="notEquals">Not Equals</option>
                          <option value="contains">Contains</option>
                          <option value="notContains">Not Contains</option>
                          <option value="greaterThan">Greater Than</option>
                          <option value="lessThan">Less Than</option>
                          <option value="isEmpty">Is Empty</option>
                          <option value="isNotEmpty">Is Not Empty</option>
                        </select>
                        <input
                          value={rule.value || ''}
                          onChange={(e) => {
                            const rules = [...(selected.props.visibilityLogic?.rules || [])];
                            rules[idx] = { ...rules[idx], value: e.target.value };
                            updateComponent(selected.id, {
                              visibilityLogic: { ...(selected.props.visibilityLogic || { action: 'show', condition: 'all' }), rules }
                            });
                          }}
                          placeholder="Match value"
                          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
                        />
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        const prev = selected.props.visibilityLogic || { action: 'show', condition: 'all', rules: [] };
                        updateComponent(selected.id, {
                          visibilityLogic: {
                            ...prev,
                            rules: [...(prev.rules || []), { fieldId: '', operator: 'equals', value: '' }]
                          }
                        });
                      }}
                      className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#4f46e5] hover:text-[#232753]"
                    >
                      Add Logic Rule
                    </button>
                  </div>
                </div>
                )}
              </div>
            )}

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                <div>
                  <p className="text-xs font-semibold text-emerald-700">Creation pipeline preserved</p>
                  <p className="text-xs text-emerald-700/90">Your form creation pipeline remains unchanged.</p>
                </div>
              </div>
            </div>
          </div>
        </aside>
        )}
      </div>
    </div>
  );
};

export default SmartProcessingStudio;
